import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Tooltip,
  useDisclosure,
  useBreakpointValue,
  SimpleGrid,
} from '@chakra-ui/react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  pulseSpeedAtom, isPausedAtom, currencyAtom, newBlockTriggerAtom, isMutedAtom,
  focusedBlockIdxAtom, satsModeAtom, genesisBlockAtom,
  pizzaDayAtom, milestoneAtom, konamiActiveAtom, konamiSloganAtom,
  explorerBlockAtom, explorerTxAtom, satsGameAtom, satsScoreAtom, satsPickupAtom, nodeMapActiveAtom, nodeCountAtom,
  hudVisibilityAtom, HUD_VISIBILITY_DEFAULT,
  searchedBlockAtom,
  welcomeMessageVisibleAtom,
  blockClickHintDismissedAtom,
  timechainViewAtom,
  ENABLE_NODE_GLOBE, ENABLE_TOUR,
} from '../state/atoms';
import { CURRENCIES } from '../hooks/useFxRates';
import { setMuted, playMilestoneSound } from '../utils/audio';
import { getRandomQuote } from '../utils/satoshiQuotes';
import { GENESIS_BLOCK, WHITEPAPER_SECTIONS, getMinedSupply, getInflationRate } from '../utils/bitcoinData';
import { NodeCountriesPanel } from './NodeCountriesPanel';
import { useAchievements, ACHIEVEMENTS } from '../hooks/useAchievements';
import { useBlockLookup } from '../hooks/useBlockLookup';
import { useBlockNotifications } from '../hooks/useBlockNotifications';
import { useSearchQuota } from '../hooks/useSearchQuota';
import { AchievementsModal } from './AchievementsModal';
import { EditableWelcome, toggleWelcomeWithDebounce } from './EditableWelcome';
import pkg from '../../package.json';

const ORANGE = '#F7931A';
const ORANGE_DIM = 'rgba(247, 147, 26, 0.7)';
const RED_ORANGE = '#FF7A1A';

// Flip to true to re-enable ?block=<h|hash> deep-link auto-search.
// Disabled to avoid rate-limit exposure from crawler-style URL hits.
const ENABLE_DEEP_LINK_SEARCH = false;

// Flip to true to re-enable browser push notifications on new blocks.
// Disabled while we sort out the prompt UX.
const ENABLE_BLOCK_NOTIFICATIONS = false;

const ORANGE_GLOW = 'rgba(247, 147, 26, 0.15)';
const CYAN = '#00e5ff';
const GREEN = '#00ff88';
const RED = '#ff4444';
const NEXT_HALVING = 1_050_000;

const fmtCurrency = (val, currency, decimals = 0) => {
  if (!val && val !== 0) return '---';
  const info = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];
  return info.symbol + val.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtVol = (vol) => {
  if (!vol) return '---';
  if (vol >= 1000) return (vol / 1000).toFixed(1) + 'K';
  return vol.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const fmtNum = (n) => (!n && n !== 0) ? '---' : n.toLocaleString('en-US');
const trimTrailingZeros = (str) => str.includes('.') ? str.replace(/\.?0+$/, '') : str;
const fmtSize = (b) => b ? trimTrailingZeros((b / 1_000_000).toFixed(2)) + ' MB' : '---';
const fmtSats = (s, decimals = 4) => (!s && s !== 0) ? '---' : trimTrailingZeros((s / 100_000_000).toFixed(decimals));
const fmtBtc = (btc, decimals = 4) => btc == null ? '---' : trimTrailingZeros(btc.toFixed(decimals));
const fmtFeeRate = (r) => r == null ? '---' : trimTrailingZeros(Number(r).toFixed(1)) + ' s/vB';

const BRACKET_SIZE = '8px';
const BRACKET_THICK = '1px';

const BracketPanel = ({ children, accentColor = ORANGE_DIM, cornerInset = '4px', ...props }) => (
  <Box
    position="relative"
    p={{ base: '10px', md: '14px' }}
    pointerEvents="auto"
    bg="rgba(0, 0, 0, 0.5)"
    backdropFilter="blur(6px)"
    {...props}

    _after={{
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.015) 2px, rgba(255,255,255,0.015) 4px)`,
      pointerEvents: 'none',
      zIndex: 1,
    }}
  >
    {}
    <Box position="absolute" top={cornerInset} left={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderTop={BRACKET_THICK} borderLeft={BRACKET_THICK} borderColor={accentColor} />
    {}
    <Box position="absolute" top={cornerInset} right={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderTop={BRACKET_THICK} borderRight={BRACKET_THICK} borderColor={accentColor} />
    {}
    <Box position="absolute" bottom={cornerInset} left={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderBottom={BRACKET_THICK} borderLeft={BRACKET_THICK} borderColor={accentColor} />
    {}
    <Box position="absolute" bottom={cornerInset} right={cornerInset} w={BRACKET_SIZE} h={BRACKET_SIZE}
      borderBottom={BRACKET_THICK} borderRight={BRACKET_THICK} borderColor={accentColor} />
    {}
    <Box position="relative" zIndex={2}>{children}</Box>
  </Box>
);

const HudTooltip = ({ label, children, placement = 'top' }) => {
  if (!label) return children;
  return (
    <Tooltip
      label={label}
      placement={placement}
      bg="black"
      color="whiteAlpha.900"
      fontSize="10px"
      fontFamily="mono"
      letterSpacing="0.04em"
      lineHeight="1.5"
      px={2}
      py="6px"
      borderRadius={0}
      border="1px solid"
      borderColor={ORANGE_DIM}
      openDelay={250}
      closeDelay={80}
      maxW="240px"
      gutter={6}
    >
      {children}
    </Tooltip>
  );
};

const TLabel = ({ children, color = 'whiteAlpha.800', tooltip }) => (
  <HudTooltip label={tooltip}>
    <Text
      fontSize="9px" color={color} fontWeight="600"
      letterSpacing="0.25em" textTransform="uppercase"
      fontFamily="mono" lineHeight="1"
      textShadow="0 0 8px rgba(255,255,255,0.3)"
      cursor={tooltip ? 'help' : undefined}
    >
      {children}
    </Text>
  </HudTooltip>
);

const TVal = ({ children, color = 'white', large, pulse, ...props }) => (
  <Text
    fontSize={large ? { base: '18px', md: '22px' } : '13px'}
    color={color} fontWeight={large ? '700' : '500'}
    fontFamily="mono" lineHeight="1.2" whiteSpace="nowrap"
    letterSpacing="0.05em"
    textShadow={large ? '0 0 12px rgba(255,255,255,0.4)' : '0 0 6px rgba(255,255,255,0.2)'}
    animation={pulse ? 'valPulse 2s ease-in-out infinite' : undefined}
    sx={pulse ? {
      '@keyframes valPulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.7 },
      },
    } : undefined}
    {...props}
  >
    {children}
  </Text>
);

const TRow = ({ label, value, color, dotted = true, tooltip }) => (
  <HudTooltip label={tooltip}>
    <Flex justify="space-between" align="baseline" gap={2} cursor={tooltip ? 'help' : undefined}>
      <TLabel>{label}</TLabel>
      {dotted && (
        <Box flex={1} borderBottom="1px dotted" borderColor="whiteAlpha.300" mb="3px" mx={1} />
      )}
      <TVal color={color}>{value}</TVal>
    </Flex>
  </HudTooltip>
);

const Rule = ({ label, color = ORANGE_DIM, tooltip }) => (
  <HudTooltip label={tooltip}>
    <Flex align="center" gap={2} my={1} cursor={tooltip ? 'help' : undefined}>
      <Box flex={1} h="1px" bg={color} />
      {label && <TLabel color={color}>{label}</TLabel>}
      {label && <Box flex={1} h="1px" bg={color} />}
    </Flex>
  </HudTooltip>
);

const CtrlBtn = ({ children, onClick, active, ...props }) => (
  <Box
    as="button" px="8px" py="4px"
    fontSize="9px" fontWeight="700" fontFamily="mono"
    letterSpacing="0.2em" textTransform="uppercase"
    bg={active ? ORANGE : 'transparent'}
    color={active ? 'black' : 'whiteAlpha.800'}
    border="1px solid"
    borderColor={active ? ORANGE : 'whiteAlpha.300'}
    cursor="pointer" transition="all 0.15s"
    _hover={{
      borderColor: active ? ORANGE : ORANGE_DIM,
      color: active ? 'black' : 'whiteAlpha.800',
      bg: active ? ORANGE : ORANGE_GLOW,
    }}
    onClick={onClick}
    {...props}
  >
    {children}
  </Box>
);

const SpeedBar = ({ active, onChange }) => (
  <HStack spacing={0}>
    {[1, 2, 3, 4, 5].map(n => (
      <Box
        key={n} as="button"
        w="24px" h="20px"
        display="flex" alignItems="center" justifyContent="center"
        fontSize="10px" fontWeight="700" fontFamily="mono"
        color={active === n ? 'black' : 'whiteAlpha.600'}
        bg={active === n ? ORANGE : 'transparent'}
        borderTop="1px solid" borderBottom="1px solid"
        borderLeft={n === 1 ? '1px solid' : 'none'}
        borderRight="1px solid"
        borderColor={active === n ? ORANGE : 'whiteAlpha.200'}
        cursor="pointer" transition="all 0.15s"
        _hover={{
          bg: active === n ? ORANGE : ORANGE_GLOW,
          color: active === n ? 'black' : 'whiteAlpha.700',
        }}
        onClick={() => onChange(n)}
      >
        {n}
      </Box>
    ))}
  </HStack>
);

const HUD_ICONS = {
  price: (
    <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="currentColor">
      <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" />
    </Box>
  ),
  block: (
    <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round">
      <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" />
      <path d="M3 7l9 5 9-5" />
      <path d="M12 12v10" />
    </Box>
  ),
  network: (
    <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="currentColor">
      <path d="M3 20h3.5V10H3v10zm7.25 0h3.5V4h-3.5v16zM17.5 20H21v-6h-3.5v6z" />
    </Box>
  ),
};

const HudToggle = ({ visible, onClick, title, iconKey }) => (
  <Box
    as="button"
    w="28px" h="28px"
    display="flex" alignItems="center" justifyContent="center"
    color={visible ? ORANGE : 'black'}
    bg={visible ? 'rgba(0,0,0,0.3)' : ORANGE}
    backdropFilter="blur(6px)"
    cursor="pointer"
    border="1px solid"
    borderColor={ORANGE}
    transition="all 0.2s"
    _hover={{
      color: visible ? 'black' : ORANGE,
      bg: visible ? ORANGE : 'rgba(0,0,0,0.3)',
    }}
    onClick={onClick}
    title={title}
    pointerEvents="auto"
  >
    {HUD_ICONS[iconKey]}
  </Box>
);

const useElapsed = (timestamp, { compact = false } = {}) => {
  const [elapsed, setElapsed] = useState('---');

  useEffect(() => {
    if (!timestamp) return;
    const tick = () => {
      const diff = Math.floor(Date.now() / 1000) - timestamp;
      if (diff < 60) setElapsed(diff + 's');
      else if (diff < 3600) setElapsed(compact
        ? Math.floor(diff / 60) + 'm'
        : Math.floor(diff / 60) + 'm ' + (diff % 60) + 's');
      else setElapsed(Math.floor(diff / 3600) + 'h ' + Math.floor((diff % 3600) / 60) + 'm');
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timestamp, compact]);

  return elapsed;
};

const TimeSinceBlock = ({ timestamp }) => {
  const elapsed = useElapsed(timestamp);
  return <TRow label="elapsed" value={elapsed} color={CYAN} tooltip="Time since this block was mined." />;
};

const BlockListRow = ({ block, isActive, isHovered, onClick, onHoverEnter, onHoverLeave }) => {
  const elapsed = useElapsed(block.timestamp, { compact: true });
  const accent = isActive ? ORANGE : isHovered ? ORANGE : 'whiteAlpha.300';
  return (
    <Box
      as="button"
      onClick={onClick}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      px={2} py="6px"
      textAlign="left"
      bg={isActive ? `${ORANGE}22` : 'transparent'}
      border="1px solid"
      borderColor={accent}
      transition="all 0.15s"
      cursor="pointer"
      _hover={{ borderColor: ORANGE, bg: `${ORANGE}11` }}
      w="100%"
    >
      <Flex justify="space-between" align="baseline" gap={2}>
        <Text fontSize="11px" fontWeight="700" fontFamily="mono"
          color={isActive ? ORANGE : 'whiteAlpha.900'}
          textShadow={isActive ? `0 0 6px ${ORANGE}66` : undefined}>
          #{fmtNum(block.height)}
        </Text>
        <Text fontSize="9px" fontFamily="mono" color={CYAN} letterSpacing="0.05em">
          {elapsed}
        </Text>
      </Flex>
      <Flex justify="space-between" align="baseline" gap={2} mt="2px">
        <Text fontSize="8px" fontFamily="mono" color="whiteAlpha.600"
          letterSpacing="0.05em" textTransform="uppercase"
          overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="60%">
          {block.pool || '—'}
        </Text>
        <Text fontSize="8px" fontFamily="mono" color="whiteAlpha.500" letterSpacing="0.05em">
          {fmtNum(block.txCount)} tx · {fmtFeeRate(block.medianFee)}
        </Text>
      </Flex>
    </Box>
  );
};

const CurrencySelector = ({ currency, setCurrency }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box position="relative" display="inline-block">
      <Text
        as="button" fontSize="9px" color={ORANGE} fontWeight="700"
        letterSpacing="0.25em" fontFamily="mono"
        cursor="pointer" _hover={{ color: '#ffb347' }} transition="color 0.2s"
        onClick={() => setIsOpen(o => !o)} whiteSpace="nowrap"
      >
        {currency} ▾
      </Text>
      {isOpen && (
        <Box
          position="absolute" top="100%" right={0} mt="4px" zIndex={50}
          bg="black" border="1px solid" borderColor={ORANGE_DIM}
          minW="90px" maxH="200px" overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bg: ORANGE_DIM },
          }}
        >
          {CURRENCIES.map(c => (
            <Box
              key={c.code} as="button" display="block" w="100%"
              px="10px" py="4px" textAlign="left" whiteSpace="nowrap"
              fontSize="9px" fontWeight="600" fontFamily="mono"
              letterSpacing="0.15em"
              color={c.code === currency ? 'black' : 'whiteAlpha.600'}
              bg={c.code === currency ? ORANGE : 'transparent'}
              cursor="pointer" transition="all 0.1s"
              _hover={{ bg: c.code === currency ? ORANGE : ORANGE_GLOW, color: c.code === currency ? 'black' : 'whiteAlpha.900' }}
              onClick={() => { setCurrency(c.code); setIsOpen(false); }}
            >
              {c.symbol} {c.code}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const convert = (usdVal, rate) => {
  if (!usdVal && usdVal !== 0) return null;
  return usdVal * rate;
};

const InfoModal = ({ isOpen, onClose, onReplayTour }) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: '3xl' }}>
    <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
    <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={{ base: 0, md: 4 }}>
      <ModalBody py={6} px={6}>
        <BracketPanel accentColor={ORANGE}>
          <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono">
            <HStack justify="space-between" align="center">
              <TLabel color={ORANGE}>SYSTEM REFERENCE</TLabel>
              <HStack spacing={2}>
                {onReplayTour && (
                  <Box
                    as="button"
                    onClick={onReplayTour}
                    px={3} py={1}
                    fontSize="10px" fontFamily="mono" fontWeight="700" letterSpacing="0.1em"
                    color={ORANGE}
                    bg="transparent"
                    border="1px solid" borderColor={ORANGE_DIM}
                    cursor="pointer"
                    transition="all 0.15s"
                    _hover={{ bg: ORANGE, color: 'black' }}
                  >
                    REPLAY TOUR
                  </Box>
                )}
                <Box as="button" onClick={onClose} color="whiteAlpha.600" _hover={{ color: ORANGE }} fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px">
                  &#x2715;
                </Box>
              </HStack>
            </HStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              {}
              <VStack spacing={4} align="stretch">
                <Box>
                  <TLabel color={ORANGE}>TESSERACT</TLabel>
                  <Text mt={1} letterSpacing="0.03em" lineHeight="1.6">
                    A 4-dimensional hypercube with 16 vertices, 32 edges, and 8 cubic cells.
                    The morphing animation is a rotation in 4D space projected into 3D.
                  </Text>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>BLOCK ORBITS</TLabel>
                  <SimpleGrid columns={1} spacing={1} mt={1}>
                    <Text>Each new block adds an orbiting cube</Text>
                    <Text>Blocks mined within 10 min cluster together</Text>
                    <Text>Recent blocks orbit closer to the center</Text>
                    <Text>Older blocks drift outward over time</Text>
                    <Text>Cube size and brightness scale with tx count</Text>
                    <Text>Hover or click cubes to see block details</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>VISUAL ENCODING</TLabel>
                  <SimpleGrid columns={1} spacing={1} mt={1}>
                    <Text><Text as="span" color={GREEN}>GREEN</Text> — Price above daily open</Text>
                    <Text><Text as="span" color={RED}>RED</Text> — Price below daily open</Text>
                    <Text><Text as="span" color={ORANGE}>ORANGE</Text> — Neutral / Bitcoin</Text>
                    <Text>Brighter edges = Higher fee pressure</Text>
                    <Text>Starfield density = Mempool tx count</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>INNER SANCTUM</TLabel>
                  <Text mt={1} letterSpacing="0.03em" lineHeight="1.6">
                    Zoom all the way in to enter the tesseract and discover words from Satoshi Nakamoto.
                  </Text>
                </Box>
              </VStack>

              {}
              <VStack spacing={4} align="stretch">
                <Box>
                  <TLabel color={ORANGE}>FLIGHT CONTROLS</TLabel>
                  <SimpleGrid columns={2} spacing={1} mt={1}>
                    <Text>WASD / Arrows — Fly</Text>
                    <Text>Q / E — Rotate L/R</Text>
                    <Text>R / F — Pitch U/D</Text>
                    <Text>Z / X — Up / Down</Text>
                    <Text>Mouse — Look around</Text>
                    <Text>Scroll — Zoom</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>SHORTCUTS</TLabel>
                  <SimpleGrid columns={2} spacing={1} mt={1}>
                    <Text>P — Play/Pause</Text>
                    <Text>M — Mute/Unmute</Text>
                    <Text>G — Genesis Block</Text>
                    <Text>L — Lightning Mode</Text>
                    <Text>SPEED 1–5 — Rotation</Text>
                    <Text>Currency — Click to cycle</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>EASTER EGGS</TLabel>
                  <SimpleGrid columns={1} spacing={1} mt={1}>
                    <Text>Konami Code — ↑↑↓↓←→←→BA</Text>
                    <Text>Type PIZZA — Pizza Day tribute</Text>
                    <Text>Type FIRE — Fireworks</Text>
                    <Text>Mobile — Tap logo 10x rapidly</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>TELEMETRY</TLabel>
                  <SimpleGrid columns={1} spacing={1} mt={1}>
                    <Text>SATS/$ — Satoshis per dollar</Text>
                    <Text>FEE RATE — Median sat/vByte</Text>
                    <Text>NEXT/30M/1H — Fee estimates</Text>
                    <Text>MEMPOOL — Unconfirmed transactions</Text>
                    <Text>DIFF — Mining difficulty progress</Text>
                    <Text>HALV — Blocks to next halving</Text>
                  </SimpleGrid>
                </Box>

                <Rule />

                <Box>
                  <TLabel color={ORANGE}>BUILT WITH</TLabel>
                  <SimpleGrid columns={1} spacing={1} mt={1}>
                    <Text>Mempool.space API</Text>
                    <Text>Coinbase API</Text>
                    <Text>Frankfurter API</Text>
                    <Text>Three.js</Text>
                    <Text>React</Text>
                  </SimpleGrid>
                </Box>
              </VStack>
            </SimpleGrid>
          </VStack>
        </BracketPanel>
      </ModalBody>
    </ModalContent>
  </Modal>
);

const ONBOARDING_SEEN_KEY = 'onboardingTourSeen';

const TOUR_STEPS = [
  {
    label: 'WELCOME',
    title: 'Welcome to Tesseract₿',
    body: 'A live window into Bitcoin. The morphing 4D shape at the center is a tesseract — a hypercube projected into 3D. The orbiting cubes around it are Bitcoin blocks, each mined roughly every 10 minutes.',
    hint: 'Everything you see is real-time data.',
  },
  {
    label: 'FLIGHT',
    title: 'Fly Around',
    body: 'Use WASD or Arrow keys to fly. Move your mouse to look around. Scroll to zoom in or out. On mobile, use the two joysticks at the bottom — left to move, right to look.',
    hint: 'Zoom all the way in to find the "inner sanctum".',
  },
  {
    label: 'DATA',
    title: 'Live Bitcoin Data',
    body: 'Price (top-left), the latest block with miner and fees (bottom-left), mempool + fee estimates + halving countdown + supply (bottom-right). All updated in real-time over a websocket.',
    hint: 'Hover any HUD label for a plain-English explanation.',
  },
  {
    label: 'EXPLORE',
    title: 'Interact',
    body: 'Click any orbiting cube to fly into it and see block details. Use the search icon (magnifier) to look up any block or transaction. The bar-chart icon opens a Fee Guide — what to pay right now. The bell enables alerts on new blocks.',
    hint: 'Press ? anytime for the full controls reference.',
  },
  {
    label: 'HIDDEN',
    title: 'Going Deeper',
    body: 'Press G for the Genesis Block. Type PIZZA, FIRE, or SATS for surprises. Unlock 21 achievements (trophy icon). Install as a native app via the banner.',
    hint: 'Ready when you are — explore freely.',
  },
];

const OnboardingTourModal = ({ isOpen, onClose, onSkip }) => {
  const [step, setStep] = useState(0);
  const totalSteps = TOUR_STEPS.length;
  const isLast = step === totalSteps - 1;

  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  const handleNext = () => {
    if (isLast) {
      onClose();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const current = TOUR_STEPS[step];

  return (
    <Modal isOpen={isOpen} onClose={onSkip} isCentered size={{ base: 'full', md: 'md' }} closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={{ base: 0, md: 4 }}>
        <ModalBody py={6} px={{ base: 5, md: 6 }}>
          <BracketPanel accentColor={ORANGE}>
            <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono" minH={{ base: 'auto', md: '300px' }}>
              <HStack justify="space-between" align="center">
                <TLabel color={ORANGE}>{current.label}</TLabel>
                <HStack spacing={1}>
                  {TOUR_STEPS.map((_, i) => (
                    <Box
                      key={i}
                      w="10px" h="3px"
                      bg={i === step ? ORANGE : 'whiteAlpha.300'}
                      transition="background 0.2s"
                    />
                  ))}
                </HStack>
              </HStack>

              <Box
                key={step}
                sx={{
                  animation: 'tourStepFade 0.35s ease-out',
                  '@keyframes tourStepFade': {
                    '0%': { opacity: 0, transform: 'translateY(4px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Text
                  fontSize={{ base: '18px', md: '22px' }}
                  fontFamily="mono"
                  fontWeight="700"
                  color={ORANGE}
                  letterSpacing="0.05em"
                  textShadow={`0 0 16px ${ORANGE}66`}
                  lineHeight="1.2"
                  mb={3}
                >
                  {current.title}
                </Text>
                <Text fontSize={{ base: '12px', md: '13px' }} color="whiteAlpha.800" lineHeight="1.7" letterSpacing="0.02em" mb={3}>
                  {current.body}
                </Text>
                <Text fontSize="10px" color="whiteAlpha.600" letterSpacing="0.05em" fontStyle="italic">
                  {current.hint}
                </Text>
              </Box>

              <Flex justify="space-between" align="center" mt={2} gap={2}>
                <Box
                  as="button"
                  px={3} py={1}
                  fontSize="10px" fontFamily="mono" fontWeight="700" letterSpacing="0.1em"
                  color="whiteAlpha.600"
                  bg="transparent"
                  cursor="pointer"
                  _hover={{ color: ORANGE }}
                  onClick={onSkip}
                >
                  SKIP TOUR
                </Box>
                <HStack spacing={2}>
                  {step > 0 && (
                    <Box
                      as="button"
                      px={4} py={2}
                      fontSize="11px" fontFamily="mono" fontWeight="700" letterSpacing="0.1em"
                      color="whiteAlpha.600"
                      bg="transparent"
                      border="1px solid" borderColor="whiteAlpha.300"
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{ color: 'white', borderColor: 'white' }}
                      onClick={handleBack}
                    >
                      BACK
                    </Box>
                  )}
                  <Box
                    as="button"
                    px={5} py={2}
                    fontSize="11px" fontFamily="mono" fontWeight="700" letterSpacing="0.1em"
                    color={ORANGE}
                    bg="transparent"
                    border="1px solid" borderColor={ORANGE}
                    cursor="pointer"
                    transition="all 0.15s"
                    _hover={{ bg: ORANGE, color: 'black' }}
                    onClick={handleNext}
                  >
                    {isLast ? "LET'S GO" : 'NEXT'}
                  </Box>
                </HStack>
              </Flex>
            </VStack>
          </BracketPanel>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const InstallModal = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'sm', md: 'md' }}>
    <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
    <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={4}>
      <ModalBody py={6} px={6}>
        <BracketPanel accentColor={ORANGE}>
          <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono">
            <HStack justify="space-between" align="center">
              <TLabel color={ORANGE}>INSTALL TESSERACT₿</TLabel>
              <Box as="button" onClick={onClose} color="whiteAlpha.600" _hover={{ color: ORANGE }} fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px">
                &#x2715;
              </Box>
            </HStack>
            <Text letterSpacing="0.03em" lineHeight="1.6">
              Add Tesseract₿ to your home screen for a full-screen, app-like experience.
            </Text>
            <Rule />
            <Box>
              <TLabel color={ORANGE}>IPHONE / IPAD</TLabel>
              <Text mt={1} letterSpacing="0.03em" lineHeight="1.6">
                Tap the Share button (rectangle with arrow), then scroll down and tap "Add to Home Screen".
              </Text>
            </Box>
            <Rule />
            <Box>
              <TLabel color={ORANGE}>ANDROID / CHROME</TLabel>
              <Text mt={1} letterSpacing="0.03em" lineHeight="1.6">
                Tap the menu (three dots), then tap "Add to Home Screen" or "Install App".
              </Text>
            </Box>
            <Rule />
            <Box>
              <TLabel color={ORANGE}>DESKTOP</TLabel>
              <Text mt={1} letterSpacing="0.03em" lineHeight="1.6">
                Click the install icon in the address bar, or use browser menu and select "Install Tesseract₿".
              </Text>
            </Box>
          </VStack>
        </BracketPanel>
      </ModalBody>
    </ModalContent>
  </Modal>
);

const fmtBlockDate = (ts) => {
  if (!ts) return '---';
  const now = Math.floor(Date.now() / 1000);
  const diff = now - ts;
  if (diff < 60) return diff + 's ago';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 2592000) return Math.floor(diff / 86400) + 'd ago';
  return new Date(ts * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const TxResultCard = ({ tx }) => {
  const isMempool = tx.source === 'mempool';
  const feeBtc = tx.fee != null ? fmtSats(tx.fee, 8) : null;
  const rate = tx.feeRate != null ? tx.feeRate.toFixed(1) : null;
  return (
    <BracketPanel accentColor={ORANGE}>
      <VStack spacing="5px" align="stretch">
        <HStack justify="space-between" align="baseline">
          <TLabel color={ORANGE}>TRANSACTION</TLabel>
          <Text fontSize="8px" color="whiteAlpha.600" fontFamily="mono" letterSpacing="0.15em">
            {isMempool ? 'MEMPOOL.SPACE' : 'ESPLORA'}
          </Text>
        </HStack>
        <HStack spacing={2} align="center">
          {tx.isCoinbase && (
            <Box px={1} py="1px" border="1px solid" borderColor={ORANGE} fontSize="8px" color={ORANGE} fontFamily="mono" letterSpacing="0.1em">
              COINBASE
            </Box>
          )}
          <Box px={1} py="1px" border="1px solid" borderColor={tx.confirmed ? '#34d399' : '#FFD93D'} fontSize="8px" color={tx.confirmed ? '#34d399' : '#FFD93D'} fontFamily="mono" letterSpacing="0.1em">
            {tx.confirmed ? 'CONFIRMED' : 'IN MEMPOOL'}
          </Box>
        </HStack>
        <Rule color="whiteAlpha.100" />
        {tx.confirmed && tx.blockHeight != null && (
          <TRow label="block" value={'#' + fmtNum(tx.blockHeight)} />
        )}
        <TRow label="size" value={(tx.size || 0).toLocaleString() + ' B'} />
        <TRow label="weight" value={tx.weight ? (tx.weight / 1000).toFixed(1) + ' kWU' : '---'} />
        {feeBtc && <TRow label="fee" value={feeBtc + ' BTC'} />}
        {rate && <TRow label="rate" value={rate + ' s/vB'} />}
        <TRow label="inputs" value={fmtNum(tx.vinCount) + (tx.totalIn ? ' / ' + fmtSats(tx.totalIn) + ' BTC' : '')} />
        <TRow label="outputs" value={fmtNum(tx.voutCount) + ' / ' + fmtSats(tx.totalOut) + ' BTC'} />
        {tx.confirmed && tx.blockTime && (
          <TRow label="mined" value={fmtBlockDate(tx.blockTime)} />
        )}
        <Rule color="whiteAlpha.100" />
        <Box>
          <TLabel color="whiteAlpha.600">TXID</TLabel>
          <Text mt={1} fontSize="9px" color="whiteAlpha.600" fontFamily="mono" wordBreak="break-all" letterSpacing="0.02em">
            {tx.txid}
          </Text>
        </Box>
      </VStack>
    </BracketPanel>
  );
};

const fmtWaitTime = (ms) => {
  if (ms <= 0) return 'any moment';
  const totalSec = Math.ceil(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m >= 1) return `~${m}m ${s}s`;
  return `${s}s`;
};

const SearchModal = ({ isOpen, onClose, initialQuery }) => {
  const [input, setInput] = useState('');
  const { block, tx, loading, error, lookup, reset, fetchBlockByHash } = useBlockLookup();
  const [txFlyLoading, setTxFlyLoading] = useState(false);
  const quota = useSearchQuota();
  const setSearchedBlock = useSetRecoilState(searchedBlockAtom);
  const setGenesisActive = useSetRecoilState(genesisBlockAtom);
  const setFocusedBlockIdx = useSetRecoilState(focusedBlockIdxAtom);
  const setExplorerTx = useSetRecoilState(explorerTxAtom);
  const initialHandledRef = useRef(null);
  const txHandledRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialQuery && initialHandledRef.current !== initialQuery) {
      initialHandledRef.current = initialQuery;
      setInput(initialQuery);
      if (quota.consume()) lookup(initialQuery);
    }
  }, [isOpen, initialQuery, lookup, quota]);

  const setExplorerBlock = useSetRecoilState(explorerBlockAtom);

  useEffect(() => {
    if (!isOpen || !block) return;
    setGenesisActive(false);
    setFocusedBlockIdx(null);
    setSearchedBlock(block);
    setExplorerBlock(block);
    setExplorerTx(null);
    reset();
    setInput('');
    initialHandledRef.current = null;
    onClose();
  }, [isOpen, block, onClose, reset, setSearchedBlock, setGenesisActive, setFocusedBlockIdx, setExplorerBlock, setExplorerTx]);

  useEffect(() => {
    if (!isOpen || !tx || !tx.confirmed || !tx.blockHash) return;
    if (txHandledRef.current === tx.txid) return;
    txHandledRef.current = tx.txid;

    let cancelled = false;
    setTxFlyLoading(true);
    (async () => {
      const containingBlock = await fetchBlockByHash(tx.blockHash);
      if (cancelled || !containingBlock) {
        if (!cancelled) setTxFlyLoading(false);
        return;
      }
      setGenesisActive(false);
      setFocusedBlockIdx(null);
      setExplorerBlock(null);
      setSearchedBlock(containingBlock);
      setExplorerTx(tx);
      setTxFlyLoading(false);
      reset();
      setInput('');
      initialHandledRef.current = null;
      onClose();
    })();
    return () => { cancelled = true; };
  }, [isOpen, tx, fetchBlockByHash, setSearchedBlock, setExplorerBlock, setExplorerTx, setGenesisActive, setFocusedBlockIdx, reset, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!quota.consume()) return;
    lookup(input);
  };

  const handleClose = () => {
    reset();
    setInput('');
    initialHandledRef.current = null;
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size={{ base: 'full', md: 'md' }}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={{ base: 0, md: 4 }}>
        <ModalBody py={6} px={6}>
          <BracketPanel accentColor={ORANGE}>
            <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono">
              <HStack justify="space-between" align="center">
                <TLabel color={ORANGE}>SEARCH</TLabel>
                <Box as="button" onClick={handleClose} color="whiteAlpha.600" _hover={{ color: ORANGE }} fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px">
                  &#x2715;
                </Box>
              </HStack>
              <Text fontSize="10px" color="whiteAlpha.600" letterSpacing="0.05em" lineHeight="1.5">
                Block height (e.g. 840000), block hash, or transaction id (64 hex).
              </Text>

              {quota.limited ? (
                <Box p={3} border="1px solid" borderColor="#FF6B6B66" bg="rgba(255, 107, 107, 0.08)">
                  <TLabel color="#FF6B6B">SEARCH LIMIT REACHED</TLabel>
                  <Text fontSize="11px" color="whiteAlpha.700" mt="4px" lineHeight="1.5" letterSpacing="0.02em">
                    You've used all {quota.max} searches in the last hour. This limit protects mempool.space from excessive requests and keeps your IP from being rate-limited.
                  </Text>
                  <Text fontSize="10px" color={ORANGE} fontFamily="mono" mt={2}>
                    NEXT SLOT AVAILABLE IN {fmtWaitTime(quota.nextAvailableMs).toUpperCase()}
                  </Text>
                </Box>
              ) : (
                <Text fontSize="9px" color="whiteAlpha.600" fontFamily="mono" letterSpacing="0.05em">
                  {quota.remaining} / {quota.max} searches remaining this hour
                </Text>
              )}
              <form onSubmit={handleSubmit}>
                <HStack spacing={2}>
                  <Box
                    as="input"
                    flex={1}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="height or hash"
                    autoFocus
                    spellCheck={false}
                    autoComplete="off"
                    px={3}
                    py={2}
                    fontSize="12px"
                    fontFamily="mono"
                    color="white"
                    bg="blackAlpha.600"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                    outline="none"
                    transition="border-color 0.15s"
                    _focus={{ borderColor: ORANGE }}
                    _placeholder={{ color: 'whiteAlpha.600' }}
                  />
                  <Box
                    as="button"
                    type="submit"
                    px={4}
                    py={2}
                    fontSize="11px"
                    fontWeight="700"
                    fontFamily="mono"
                    letterSpacing="0.1em"
                    color={quota.limited ? 'whiteAlpha.600' : ORANGE}
                    bg="transparent"
                    border="1px solid"
                    borderColor={quota.limited ? 'whiteAlpha.200' : ORANGE_DIM}
                    cursor={quota.limited || loading ? 'not-allowed' : 'pointer'}
                    transition="all 0.15s"
                    _hover={quota.limited ? {} : { bg: ORANGE, color: 'black' }}
                    disabled={loading || quota.limited}
                  >
                    {loading ? '...' : 'GO'}
                  </Box>
                </HStack>
              </form>
              {error === 'invalid' && (
                <Text fontSize="10px" color="#FF6B6B" fontFamily="mono">
                  Input must be a block height (number) or block hash (64 hex).
                </Text>
              )}
              {error === 'notfound' && (
                <Text fontSize="10px" color="#FF6B6B" fontFamily="mono">
                  No block or transaction found on mempool.space or esplora.
                </Text>
              )}
              {loading && (
                <Text fontSize="10px" color="whiteAlpha.600" fontFamily="mono">
                  Searching…
                </Text>
              )}
              {txFlyLoading && (
                <Text fontSize="10px" color={ORANGE} fontFamily="mono">
                  Locating block…
                </Text>
              )}
              {tx && !txFlyLoading && <TxResultCard tx={tx} />}
            </VStack>
          </BracketPanel>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const WhitepaperModal = ({ isOpen, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageRanges, setPageRanges] = useState(null);
  const contentRef = useRef(null);

  const allBlocks = React.useMemo(() => {
    const blocks = [];
    WHITEPAPER_SECTIONS.forEach((section, idx) => {
      if (idx === 0) {
        blocks.push({ type: 'header', content: section.label, meta: section.text });
      } else if (section.label === 'Abstract') {
        blocks.push({ type: 'abstract', content: section.text, label: 'Abstract.' });
      } else {
        blocks.push({ type: 'title', content: section.label });
        section.text.split('\n\n').forEach((para) => {
          blocks.push({ type: 'paragraph', content: para });
        });
      }
    });
    blocks.push({ type: 'footer', content: 'bitcoin.org/bitcoin.pdf' });
    return blocks;
  }, []);

  // Phase: null = measuring, array = ready
  useEffect(() => {
    if (!isOpen) { setPageRanges(null); setCurrentPage(0); return; }
    if (pageRanges) return;
    const raf = requestAnimationFrame(() => {
      const el = contentRef.current;
      if (!el) return;
      const style = getComputedStyle(el);
      const BOTTOM_BUFFER = 12;
      const availH = el.clientHeight - parseFloat(style.paddingTop) - BOTTOM_BUFFER;
      const blockEls = el.querySelectorAll('[data-wp-block]');
      if (!blockEls.length) return;
      const GAP = 16;
      const ranges = [];
      let start = 0;
      let usedH = 0;
      const blocks = Array.from(blockEls);
      blocks.forEach((child, i) => {
        const h = child.offsetHeight + (i > start ? GAP : 0);
        if (usedH + h > availH && i > start) {
          ranges.push([start, i]);
          start = i;
          usedH = child.offsetHeight;
        } else {
          usedH += h;
        }
      });
      if (start < blocks.length) ranges.push([start, blocks.length]);

      // Prevent orphaned titles: if a page ends with a lone title, pull it to the next page
      for (let r = 0; r < ranges.length - 1; r++) {
        const [s, e] = ranges[r];
        if (e - 1 > s && allBlocks[e - 1]?.type === 'title') {
          ranges[r] = [s, e - 1];
          ranges[r + 1] = [e - 1, ranges[r + 1][1]];
        }
      }
      setPageRanges(ranges);
      setCurrentPage(0);
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen, pageRanges, allBlocks]);

  const measuring = !pageRanges;
  const totalPages = pageRanges ? pageRanges.length : 0;
  const displayBlocks = measuring
    ? allBlocks
    : allBlocks.slice(pageRanges[currentPage]?.[0] || 0, pageRanges[currentPage]?.[1] || 0);

  const flip = useCallback((dir) => {
    setCurrentPage((p) => {
      const next = p + dir;
      return next >= 0 && next < totalPages ? next : p;
    });
  }, [totalPages]);

  useEffect(() => {
    if (!isOpen || measuring) return;
    const handleKey = (e) => {
      if (e.code === 'ArrowLeft') flip(-1);
      else if (e.code === 'ArrowRight') flip(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, measuring, flip]);

  const renderBlock = (block, i) => {
    switch (block.type) {
      case 'header':
        return (
          <VStack spacing={2} textAlign="center" py={2}>
            <Text
              fontSize={{ base: '14px', md: '17px' }} color={ORANGE}
              fontWeight="700" letterSpacing="0.15em" textTransform="uppercase"
              fontFamily="mono" lineHeight="1.4" textShadow={`0 0 8px ${ORANGE}44`}
            >
              {block.content}
            </Text>
            {block.meta.split('\n').map((line, li) => (
              <Text
                key={li} fontSize={{ base: '12px', md: '13px' }}
                color="whiteAlpha.600" fontFamily="mono" letterSpacing="0.05em"
                lineHeight="1.2"
              >
                {line}
              </Text>
            ))}
          </VStack>
        );
      case 'title':
        return (
          <Text
            fontSize={{ base: '12px', md: '14px' }} color={ORANGE}
            fontWeight="600" letterSpacing="0.25em" textTransform="uppercase"
            fontFamily="mono" lineHeight="1" textShadow={`0 0 8px ${ORANGE}44`}
          >
            {block.content}
          </Text>
        );
      case 'abstract':
        return (
          <Text
            letterSpacing="0.03em" lineHeight="1.8"
            fontSize={{ base: '11px', md: '12px' }}
            color="whiteAlpha.700" fontFamily="mono"
            mx={{ base: '4%', md: '8%' }}
          >
            <Text as="span" fontWeight="700" color={ORANGE}>{block.label} </Text>
            {block.content}
          </Text>
        );
      case 'diagram':
        return (
          <Box
            px={3} py={3} bg="rgba(247, 147, 26, 0.04)"
            border="1px solid" borderColor="whiteAlpha.100" borderRadius="2px"
            overflowX="auto"
          >
            <Text
              fontFamily="mono" fontSize={{ base: '10px', md: '13px' }}
              lineHeight="1.5" color={ORANGE} whiteSpace="pre" letterSpacing="0"
            >
              {block.content}
            </Text>
          </Box>
        );
      case 'paragraph':
        return (
          <Text
            letterSpacing="0.03em" lineHeight="1.8"
            fontSize={{ base: '12px', md: '13px' }}
            color="whiteAlpha.700" fontFamily="mono"
          >
            {block.content}
          </Text>
        );
      case 'footer':
        return (
          <Box>
            <Box h="1px" bg={ORANGE_DIM} mb={2} />
            <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600"
              letterSpacing="0.05em" textAlign="center">
              {block.content}
            </Text>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: '3xl' }}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent
        bg="black" border="1px solid" borderColor={ORANGE_DIM}
        borderRadius={0} mx={{ base: 0, md: 4 }}
        h={{ base: '100vh', md: '85vh' }} maxH={{ base: '100vh', md: '85vh' }}
      >
        <ModalBody py={0} px={0} display="flex" flexDirection="column" h="100%" overflow="hidden">
          <Flex justify="space-between" align="center" px={5} pt={4} pb={2} flexShrink={0}>
            <HStack spacing={3} align="center">
              <Box as="svg" viewBox="0 0 273.6 360" w="24px" h="32px" flexShrink={0} fill={ORANGE} opacity={0.8}>
                <path d="M217.021,167.042c18.631-9.483,30.288-26.184,27.565-54.007c-3.667-38.023-36.526-50.773-78.006-54.404l-0.008-52.741h-32.139l-0.009,51.354c-8.456,0-17.076,0.166-25.657,0.338L108.76,5.897l-32.11-0.003l-0.006,52.728c-6.959,0.142-13.793,0.277-20.466,0.277v-0.156l-44.33-0.018l0.006,34.282c0,0,23.734-0.446,23.343-0.013c13.013,0.009,17.262,7.559,18.484,14.076l0.01,60.083v84.397c-0.573,4.09-2.984,10.625-12.083,10.637c0.414,0.364-23.379-0.004-23.379-0.004l-6.375,38.335h41.817c7.792,0.009,15.448,0.13,22.959,0.19l0.028,53.338l32.102,0.009l-0.009-52.779c8.832,0.18,17.357,0.258,25.684,0.247l-0.009,52.532h32.138l0.018-53.249c54.022-3.1,91.842-16.697,96.544-67.385C266.916,192.612,247.692,174.396,217.021,167.042z M109.535,95.321c18.126,0,75.132-5.767,75.14,32.064c-0.008,36.269-56.996,32.032-75.14,32.032V95.321z M109.521,262.447l0.014-70.672c21.778-0.006,90.085-6.261,90.094,35.32C199.638,266.971,131.313,262.431,109.521,262.447z" />
              </Box>
              <VStack spacing={0} align="flex-start">
                <TLabel color={ORANGE}>BITCOIN: A PEER-TO-PEER ELECTRONIC CASH SYSTEM</TLabel>
                <Text fontSize="9px" color="whiteAlpha.600" letterSpacing="0.1em" fontFamily="mono">
                  Satoshi Nakamoto — October 31, 2008
                </Text>
              </VStack>
            </HStack>
            <Box
              as="button" onClick={onClose}
              color="whiteAlpha.600" _hover={{ color: ORANGE }}
              fontSize="18px" lineHeight="1" cursor="pointer" flexShrink={0} ml={3}
            >
              &#x2715;
            </Box>
          </Flex>

          <Box h="1px" bg={ORANGE_DIM} mx={4} my="2px" flexShrink={0} />

          <Box
            ref={contentRef} flex={1} overflow="hidden"
            px={{ base: 4, md: 6 }} pt={4} pb={8}
            display="flex" flexDirection="column" gap="16px"
            opacity={measuring ? 0 : 1}
            transition={measuring ? 'none' : 'opacity 0.2s'}
          >
            {displayBlocks.map((block, i) => (
              <Box key={measuring ? `m-${i}` : `p${currentPage}-${i}`} data-wp-block>
                {renderBlock(block, i)}
              </Box>
            ))}
          </Box>

          <Box h="1px" bg={ORANGE_DIM} mx={4} flexShrink={0} />

          <Flex justify="space-between" align="center" px={5} py={3} flexShrink={0}>
            <Box
              as="button" onClick={() => flip(-1)}
              color={currentPage > 0 ? 'whiteAlpha.700' : 'whiteAlpha.200'}
              cursor={currentPage > 0 ? 'pointer' : 'default'}
              _hover={currentPage > 0 ? { color: ORANGE } : {}}
              fontSize="11px" fontFamily="mono" fontWeight="700" letterSpacing="0.15em"
              transition="color 0.2s" pointerEvents="auto"
            >
              &#9664; PREV
            </Box>
            <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.15em">
              {measuring ? '' : `${currentPage + 1} / ${totalPages}`}
            </Text>
            <Box
              as="button" onClick={() => flip(1)}
              color={currentPage < totalPages - 1 ? 'whiteAlpha.700' : 'whiteAlpha.200'}
              cursor={currentPage < totalPages - 1 ? 'pointer' : 'default'}
              _hover={currentPage < totalPages - 1 ? { color: ORANGE } : {}}
              fontSize="11px" fontFamily="mono" fontWeight="700" letterSpacing="0.15em"
              transition="color 0.2s" pointerEvents="auto"
            >
              NEXT &#9654;
            </Box>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const QUOTE_DELAY = 500;

const SatoshiQuoteOverlay = ({ isInsideCube }) => {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState({ text: '', author: '' });
  const timerRef = useRef(null);

  useEffect(() => {
    if (isInsideCube) {
      const quote = getRandomQuote();
      timerRef.current = setTimeout(() => {
        setContent(quote);
        setVisible(true);
      }, QUOTE_DELAY);
    } else {
      setVisible(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isInsideCube]);

  if (!visible) return null;

  return (
    <Flex
      position="absolute"
      bottom={{ base: '160px', md: '120px' }}
      left="50%"
      transform="translateX(-50%)"
      maxW={{ base: '85vw', md: '500px' }}
      pointerEvents="none"
      zIndex={15}
      animation="quoteFadeIn 1.5s ease-out forwards"
      sx={{
        '@keyframes quoteFadeIn': {
          '0%': { opacity: 0, transform: 'translateX(-50%) translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
        },
      }}
    >
      <BracketPanel accentColor={ORANGE_DIM} bg="rgba(0,0,0,0.5)" cornerInset="4px">
        <VStack spacing={2} align="center">
          <Text
            fontSize="11px" fontFamily="mono" letterSpacing="0.4em"
            color={ORANGE_DIM} fontWeight="600" textTransform="uppercase"
          >
            {content.author}
          </Text>
          <Text
            fontSize={{ base: '12px', md: '14px' }}
            fontFamily="mono"
            fontWeight="700"
            color="whiteAlpha.800"
            fontStyle="italic"
            lineHeight="1.6"
            letterSpacing="0.03em"
            textAlign="center"
            textShadow={`0 0 20px ${ORANGE}22`}
          >
            {content.text}
          </Text>
        </VStack>
      </BracketPanel>
    </Flex>
  );
};


const GOLD = '#FFD700';

const GenesisOverlay = ({ active, onClose }) => {
  if (!active) return null;
  return (
    <Flex
      position="absolute" inset={0}
      align="flex-start" justify="center"
      pt={{ base: '80px', md: '100px' }}
      pointerEvents="none" zIndex={20}
      sx={{
        animation: 'genesisOverlayFade 0.8s ease-out 0.6s both',
        '@keyframes genesisOverlayFade': {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      <BracketPanel accentColor={GOLD} bg="rgba(0,0,0,0.5)" cornerInset="4px">
        <VStack spacing={3} align="center" maxW="500px">
          <HStack justify="space-between" align="center" w="100%">
            <TLabel color={GOLD}>GENESIS BLOCK #0</TLabel>
            <Box
              as="button" onClick={onClose} pointerEvents="auto"
              color="whiteAlpha.600" _hover={{ color: GOLD }}
              fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px"
            >
              &#x2715;
            </Box>
          </HStack>
          <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.700" letterSpacing="0.1em">
            January 3, 2009
          </Text>
          <Box w="40px" h="1px" bg={GOLD + '66'} />
          <Text
            fontSize={{ base: '13px', md: '16px' }} fontFamily="mono" fontWeight="700"
            color={GOLD} fontStyle="italic" textAlign="center" lineHeight="1.6"
            letterSpacing="0.03em"
            textShadow="0 0 20px rgba(255, 215, 0, 0.3)"
          >
            "{GENESIS_BLOCK.coinbaseMessage}"
          </Text>
          <Box w="40px" h="1px" bg={GOLD + '66'} />
          <Text fontSize="7px" fontFamily="mono" color="whiteAlpha.600" wordBreak="break-all" textAlign="center">
            {GENESIS_BLOCK.hash}
          </Text>
        </VStack>
      </BracketPanel>
    </Flex>
  );
};

const PizzaDayToast = ({ active, onClose }) => {
  if (!active) return null;
  return (
    <Flex
      position="absolute" top="80px" left="50%" transform="translateX(-50%)"
      pointerEvents="none" zIndex={20}
      animation="quoteFadeIn 1.5s ease-out forwards"
    >
      <BracketPanel accentColor="#FFD700" bg="rgba(0,0,0,0.5)" cornerInset="4px">
        <VStack spacing={1} align="center">
          <HStack justify="space-between" w="100%">
            <Box w="18px" />
            <Text fontSize="20px">&#127829;&#127829;</Text>
            <Box
              as="button" onClick={onClose} pointerEvents="auto"
              color="whiteAlpha.600" _hover={{ color: '#FFD700' }}
              fontSize="16px" lineHeight="1" cursor="pointer"
            >
              &#x2715;
            </Box>
          </HStack>
          <TLabel color="#FFD700">BITCOIN PIZZA DAY</TLabel>
          <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.05em">
            10,000 BTC for two pizzas
          </Text>
          <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600">
            May 22, 2010
          </Text>
        </VStack>
      </BracketPanel>
    </Flex>
  );
};

const MilestoneOverlay = ({ active, blockHeight }) => {
  if (!active) return null;
  return (
    <Flex
      position="absolute" inset={0} align="flex-start" justify="center"
      pt="80px"
      pointerEvents="none" zIndex={25}
      animation="milestoneFlash 4s ease-out forwards"
      sx={{
        '@keyframes milestoneFlash': {
          '0%': { opacity: 0 },
          '10%': { opacity: 1 },
          '60%': { opacity: 1 },
          '100%': { opacity: 0 },
        },
      }}
    >
      <VStack spacing={2}>
        <Text fontSize="9px" fontFamily="mono" color={ORANGE_DIM} fontWeight="500" letterSpacing="0.5em">
          MILESTONE BLOCK
        </Text>
        <Text fontSize={{ base: '36px', md: '56px' }} fontWeight="900" fontFamily="mono"
          letterSpacing="0.15em" color={ORANGE}
          textShadow={`0 0 60px ${ORANGE}88, 0 0 120px ${ORANGE}44`}>
          {blockHeight ? fmtNum(blockHeight) : ''}
        </Text>
        <Text fontSize="11px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.4em">
          BLOCK CELEBRATION
        </Text>
      </VStack>
    </Flex>
  );
};

const feeColor = (satVb) => {
  if (satVb == null) return '#666';
  if (satVb < 2) return '#4ade80';
  if (satVb < 5) return '#84cc16';
  if (satVb < 15) return '#facc15';
  if (satVb < 50) return '#fb923c';
  if (satVb < 150) return '#f87171';
  return '#ef4444';
};

const fmtMinutes = (m) => {
  if (m < 60) return `~${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `~${h}h ${rem}m` : `~${h}h`;
};

const FeeHeatmapModal = ({ isOpen, onClose, blocks, fees, liveBlock0TxCount, onTrackMempoolBlock }) => {
  const loading = !blocks && !fees;
  const hasBlocks = blocks && blocks.length > 0;
  const hasFees = fees && fees.fastestFee != null;

  useEffect(() => {
    if (isOpen && onTrackMempoolBlock) onTrackMempoolBlock(0);
  }, [isOpen, onTrackMempoolBlock]);

  const fmtFee = (v) => v == null ? '--' : trimTrailingZeros(Number(v).toFixed(1));

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: 'xl' }}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={{ base: 0, md: 4 }}>
        <ModalBody py={6} px={{ base: 4, md: 6 }}>
          <BracketPanel accentColor={ORANGE}>
            <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono">
              <HStack justify="space-between" align="center">
                <TLabel color={ORANGE}>FEE GUIDE</TLabel>
                <Box as="button" onClick={onClose} color="whiteAlpha.600" _hover={{ color: ORANGE }} fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px">
                  &#x2715;
                </Box>
              </HStack>

              <Text fontSize={{ base: '10px', md: '11px' }} color="whiteAlpha.800" lineHeight="1.6" letterSpacing="0.02em">
                When you send Bitcoin, you pick a fee rate (sats per virtual byte). Higher fee = faster confirmation. These are the rates miners are picking up right now.
              </Text>

              {loading && (
                <Text fontSize="11px" color="whiteAlpha.600" fontFamily="mono">Waiting for fee data from websocket…</Text>
              )}

              {hasFees && (
                <>
                  <Rule label="pay this to confirm" />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <Box p={3} border="1px solid" borderColor="#4ade8044" bg="rgba(74, 222, 128, 0.05)">
                      <TLabel color="#4ade80">FAST</TLabel>
                      <Text fontSize="10px" color="whiteAlpha.700" mt="2px">Next block · ~10 min</Text>
                      <Text fontSize="20px" color="#4ade80" fontWeight="700" fontFamily="mono" lineHeight="1.2" mt={1}>
                        {fmtFee(fees.fastestFee)} <Text as="span" fontSize="11px" color="whiteAlpha.700">s/vB</Text>
                      </Text>
                    </Box>
                    <Box p={3} border="1px solid" borderColor="#facc1544" bg="rgba(250, 204, 21, 0.05)">
                      <TLabel color="#facc15">MEDIUM</TLabel>
                      <Text fontSize="10px" color="whiteAlpha.700" mt="2px">~30 min</Text>
                      <Text fontSize="20px" color="#facc15" fontWeight="700" fontFamily="mono" lineHeight="1.2" mt={1}>
                        {fmtFee(fees.halfHourFee)} <Text as="span" fontSize="11px" color="whiteAlpha.700">s/vB</Text>
                      </Text>
                    </Box>
                    <Box p={3} border="1px solid" borderColor="#fb923c44" bg="rgba(251, 146, 60, 0.05)">
                      <TLabel color="#fb923c">SLOW</TLabel>
                      <Text fontSize="10px" color="whiteAlpha.700" mt="2px">~1 hour</Text>
                      <Text fontSize="20px" color="#fb923c" fontWeight="700" fontFamily="mono" lineHeight="1.2" mt={1}>
                        {fmtFee(fees.hourFee)} <Text as="span" fontSize="11px" color="whiteAlpha.700">s/vB</Text>
                      </Text>
                    </Box>
                  </SimpleGrid>
                </>
              )}

              {hasBlocks && (
                <>
                  <Rule label="what's waiting to be mined" />
                  <VStack spacing="6px" align="stretch">
                    {blocks.slice(0, 8).map((b, i) => {
                      const mins = (i + 1) * 10;
                      const median = b.medianFee;
                      const fillPct = Math.min(100, (b.blockVSize / 1_000_000) * 100);
                      return (
                        <Flex key={i} align="center" gap={{ base: 2, md: 3 }} flexWrap="wrap">
                          <Box minW={{ base: '60px', md: '70px' }}>
                            <Text fontSize={{ base: '10px', md: '11px' }} color="whiteAlpha.800" fontFamily="mono" fontWeight="600">
                              {fmtMinutes(mins)}
                            </Text>
                          </Box>
                          <Box flex={1} minW="120px" position="relative" h="14px" bg="whiteAlpha.100" border="1px solid" borderColor="whiteAlpha.200" overflow="hidden">
                            <Box
                              position="absolute" top={0} left={0} h="100%"
                              w={`${fillPct}%`}
                              bg={feeColor(median)}
                              opacity={0.75}
                              transition="width 0.4s ease-out"
                            />
                          </Box>
                          <Text fontSize={{ base: '9px', md: '10px' }} color={feeColor(median)} fontFamily="mono" fontWeight="700" minW="55px" textAlign="right">
                            {fmtFee(median)} s/vB
                          </Text>
                          <Text fontSize={{ base: '8px', md: '9px' }} color="whiteAlpha.600" fontFamily="mono" minW="60px" textAlign="right">
                            {fmtNum(b.nTx)} txs
                          </Text>
                        </Flex>
                      );
                    })}
                  </VStack>

                  <Rule label="how to read this" />
                  <VStack spacing={2} align="stretch" fontSize={{ base: '9px', md: '10px' }} color="whiteAlpha.800" lineHeight="1.6">
                    <Text>
                      <Text as="span" color={ORANGE}>Each row = one future block (~10 min apart).</Text> Pay the listed rate (or more) and miners will likely include your tx in that window.
                    </Text>
                    <Text>
                      <Text as="span" color={ORANGE}>Bar length = how full the block is.</Text> A full bar means that block is packed; partial means miners still have room.
                    </Text>
                    <Text>
                      <Text as="span" color={ORANGE}>Bar color = fee level.</Text> Green cheap, red expensive. When the mempool is calm, everything's green and you can pay 1 s/vB. During congestion, the front blocks turn orange/red.
                    </Text>
                    <Text>
                      <Text as="span" color={ORANGE}>Why multiple rows can all have the same fee rate.</Text> A block only fits ~1 MB of transactions. When the network is quiet, most txs pay the floor rate, so several upcoming blocks share it. Miners pick in rough arrival order within the same rate — there's no "better" tx at the same rate.
                    </Text>
                  </VStack>
                </>
              )}

              {blocks && blocks.length === 0 && (
                <Text fontSize="11px" color="whiteAlpha.600" fontFamily="mono">
                  Mempool is empty — pay 1 s/vB and you're in the next block.
                </Text>
              )}

            </VStack>
          </BracketPanel>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const getBrowser = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
};

const NotificationHelpModal = ({ isOpen, onClose, permission }) => {
  const browser = getBrowser();
  const dismissed = permission !== 'denied';

  const steps = {
    chrome: [
      'Click the lock (or tune) icon at the left of the address bar',
      'Click "Site settings"',
      'Find "Notifications" and set it to "Allow"',
      'Reload this page',
    ],
    edge: [
      'Click the lock icon at the left of the address bar',
      'Click "Permissions for this site"',
      'Set "Notifications" to "Allow"',
      'Reload this page',
    ],
    safari: [
      'Open Safari menu → Settings (or Preferences)',
      'Go to Websites → Notifications',
      'Find this site in the list, set to "Allow"',
      'Reload this page',
    ],
    firefox: [
      'Click the lock icon at the left of the address bar',
      'Clear the notifications permission (or set to Allow)',
      'Reload this page',
    ],
    other: [
      'Open your browser\'s site settings for this page',
      'Find the Notifications permission and set it to Allow',
      'Reload this page',
    ],
  };

  const browserSteps = steps[browser] || steps.other;
  const browserLabel = { chrome: 'Chrome', edge: 'Edge', safari: 'Safari', firefox: 'Firefox', other: 'your browser' }[browser];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: 'md' }}>
      <ModalOverlay bg="blackAlpha.900" backdropFilter="blur(8px)" />
      <ModalContent bg="black" border="1px solid" borderColor={ORANGE_DIM} borderRadius={0} mx={{ base: 0, md: 4 }}>
        <ModalBody py={6} px={6}>
          <BracketPanel accentColor={ORANGE}>
            <VStack spacing={4} align="stretch" fontSize="12px" color="whiteAlpha.700" fontFamily="mono">
              <HStack justify="space-between" align="center">
                <TLabel color={ORANGE}>ENABLE NOTIFICATIONS</TLabel>
                <Box as="button" onClick={onClose} color="whiteAlpha.600" _hover={{ color: ORANGE }} fontSize="18px" lineHeight="1" cursor="pointer" mr="-2px" mt="-2px">
                  &#x2715;
                </Box>
              </HStack>
              <Text fontSize="11px" color="whiteAlpha.600" lineHeight="1.6" letterSpacing="0.02em">
                {dismissed
                  ? `You dismissed the notification prompt. ${browserLabel} now blocks automatic re-prompts for a while — you'll need to enable it manually.`
                  : `Notifications are blocked for this site. Re-enable them in ${browserLabel}'s site settings.`}
              </Text>
              <Rule />
              <Box>
                <TLabel color={ORANGE}>STEPS FOR {browserLabel.toUpperCase()}</TLabel>
                <VStack spacing={2} align="stretch" mt={2}>
                  {browserSteps.map((step, i) => (
                    <HStack key={i} align="flex-start" spacing={3}>
                      <Text fontSize="10px" color={ORANGE} fontWeight="700" fontFamily="mono" minW="18px">
                        {(i + 1).toString().padStart(2, '0')}
                      </Text>
                      <Text fontSize="11px" color="whiteAlpha.700" lineHeight="1.5" letterSpacing="0.02em">
                        {step}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Rule />
              <HStack spacing={2} justify="flex-end">
                <Box
                  as="button"
                  px={4} py={2}
                  fontSize="11px" fontWeight="700" fontFamily="mono" letterSpacing="0.1em"
                  color="whiteAlpha.600"
                  bg="transparent"
                  border="1px solid" borderColor="whiteAlpha.300"
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ color: 'white', borderColor: 'white' }}
                  onClick={onClose}
                >
                  CLOSE
                </Box>
                <Box
                  as="button"
                  px={4} py={2}
                  fontSize="11px" fontWeight="700" fontFamily="mono" letterSpacing="0.1em"
                  color={ORANGE}
                  bg="transparent"
                  border="1px solid" borderColor={ORANGE}
                  cursor="pointer"
                  transition="all 0.15s"
                  _hover={{ bg: ORANGE, color: 'black' }}
                  onClick={() => window.location.reload()}
                >
                  RELOAD PAGE
                </Box>
              </HStack>
            </VStack>
          </BracketPanel>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const InstallBanner = ({ visible, onInstall, onDismiss }) => {
  if (!visible) return null;
  return (
    <Box
      position="fixed"
      bottom={{ base: '90px', md: '70px' }}
      left="50%"
      transform="translateX(-50%)"
      zIndex={60}
      px={4}
      py={3}
      bg="rgba(8,8,12,0.95)"
      backdropFilter="blur(12px)"
      border="1px solid"
      borderColor={ORANGE_DIM}
      boxShadow={`0 0 30px ${ORANGE}33, 0 8px 24px rgba(0,0,0,0.5)`}
      maxW="340px"
      w="calc(100vw - 32px)"
      sx={{
        animation: 'installSlide 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)',
        '@keyframes installSlide': {
          '0%': { opacity: 0, transform: 'translate(-50%, 20px)' },
          '100%': { opacity: 1, transform: 'translate(-50%, 0)' },
        },
      }}
    >
      <HStack spacing={3} align="center">
        <Box fontSize="20px" color={ORANGE} flexShrink={0}>
          <Box as="svg" viewBox="0 0 24 24" w="20px" h="20px" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round" />
          </Box>
        </Box>
        <VStack spacing={0} align="stretch" flex={1}>
          <Text fontSize="11px" color={ORANGE} fontFamily="mono" fontWeight="700" letterSpacing="0.1em">
            INSTALL TESSERACT₿
          </Text>
          <Text fontSize="10px" color="whiteAlpha.600" fontFamily="mono" lineHeight="1.4">
            Launch from your home screen
          </Text>
        </VStack>
        <Box
          as="button"
          px={3} py={1}
          fontSize="10px" fontWeight="700" fontFamily="mono"
          letterSpacing="0.1em"
          color={ORANGE}
          bg="transparent"
          border="1px solid"
          borderColor={ORANGE}
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ bg: ORANGE, color: 'black' }}
          onClick={onInstall}
        >
          INSTALL
        </Box>
        <Box
          as="button"
          color="whiteAlpha.600"
          _hover={{ color: ORANGE }}
          fontSize="16px"
          lineHeight="1"
          cursor="pointer"
          px={1}
          onClick={onDismiss}
          title="Dismiss"
        >
          &#x2715;
        </Box>
      </HStack>
    </Box>
  );
};

const Crosshair = () => (
  <Box
    position="absolute"
    top="50%" left="50%"
    transform="translate(-50%, -50%)"
    pointerEvents="none"
    opacity={0.12}
  >
    {}
    <Box position="absolute" top="50%" left="-20px" w="40px" h="1px" bg="white" transform="translateY(-50%)" />
    {}
    <Box position="absolute" left="50%" top="-20px" w="1px" h="40px" bg="white" transform="translateX(-50%)" />
    {}
    <Box position="absolute" top="50%" left="50%" w="3px" h="3px" bg={ORANGE} transform="translate(-50%, -50%)" opacity={0.6} />
  </Box>
);

const fmtVbps = (v) => {
  if (v == null) return '---';
  if (v >= 1000) return (v / 1000).toFixed(1) + ' kvB/s';
  return Math.round(v) + ' vB/s';
};

export const CelestialHud = ({ price, prevPrice, stats, loading, block, blockHistory = [], blockLoading, fxRates, isNewBlock, fees, difficulty, onResetCamera, hoveredCubeBlock, hoveredCubeScreenPos, isInsideCube, mempoolTxCount, mempoolBlocks, vBytesPerSecond, trackedTxData, liveBlock0TxCount, trackTx, trackMempoolBlock, onLogoTap, cameraAchievementRef }) => {
  const [pulseSpeed, setPulseSpeed] = useRecoilState(pulseSpeedAtom);
  const [isPaused, setIsPaused] = useRecoilState(isPausedAtom);
  const [isMuted, setIsMuted] = useRecoilState(isMutedAtom);
  const setNewBlockTrigger = useSetRecoilState(newBlockTriggerAtom);
  const [currency, setCurrency] = useRecoilState(currencyAtom);
  const [satsMode, setSatsMode] = useRecoilState(satsModeAtom);
  const [genesisActive, setGenesisActive] = useRecoilState(genesisBlockAtom);
  const [pizzaDay, setPizzaDay] = useRecoilState(pizzaDayAtom);
  const milestone = useRecoilValue(milestoneAtom);
  const setMilestone = useSetRecoilState(milestoneAtom);
  const setKonami = useSetRecoilState(konamiActiveAtom);
  const konamiSlogan = useRecoilValue(konamiSloganAtom);
  const [explorerBlock, setExplorerBlock] = useRecoilState(explorerBlockAtom);
  const [explorerTx, setExplorerTx] = useRecoilState(explorerTxAtom);
  const satsGame = useRecoilValue(satsGameAtom);
  const satsScore = useRecoilValue(satsScoreAtom);
  const [satsPickups, setSatsPickups] = useRecoilState(satsPickupAtom);
  const setSatsGame = useSetRecoilState(satsGameAtom);
  const [nodeMapActive, setNodeMapActive] = useRecoilState(nodeMapActiveAtom);
  const nodeCount = useRecoilValue(nodeCountAtom);
  const setTimechainView = useSetRecoilState(timechainViewAtom);
  const [searchedBlock, setSearchedBlock] = useRecoilState(searchedBlockAtom);
  const [blockClickHintDismissed, setBlockClickHintDismissed] = useRecoilState(blockClickHintDismissedAtom);

  useEffect(() => {
    if (!blockClickHintDismissed && explorerBlock) {
      setBlockClickHintDismissed(true);
    }
  }, [explorerBlock, blockClickHintDismissed, setBlockClickHintDismissed]);

  useEffect(() => {
    if (!trackTx) return;
    trackTx(explorerTx?.txid || null);
  }, [explorerTx?.txid, trackTx]);

  const prevGenesisRef = useRef(genesisActive);
  useEffect(() => {
    const wasActive = prevGenesisRef.current;
    prevGenesisRef.current = genesisActive;
    if (genesisActive && !wasActive) {
      setExplorerBlock(GENESIS_BLOCK);
    } else if (!genesisActive && wasActive) {
      setExplorerBlock(null);
    }
  }, [genesisActive, setExplorerBlock]);
  const [hudVisibility, setHudVisibility] = useRecoilState(hudVisibilityAtom);
  const toggleHud = useCallback((key) => setHudVisibility(v => ({ ...v, [key]: !v[key] })), [setHudVisibility]);
  const [welcomeVisible, setWelcomeVisible] = useRecoilState(welcomeMessageVisibleAtom);
  useEffect(() => {
    try { localStorage.setItem('tesseractWelcomeVisible', String(welcomeVisible)); } catch {}
  }, [welcomeVisible]);
  const [hoveredBlockIdx, setHoveredBlockIdx] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleReset = useCallback(() => { setPulseSpeed(0.1); setIsPaused(false); setExplorerBlock(null); setExplorerTx(null); setHudVisibility(HUD_VISIBILITY_DEFAULT); setTimechainView(false); setNodeMapActive(false); onResetCamera?.(); }, [setPulseSpeed, setIsPaused, setExplorerBlock, setExplorerTx, setHudVisibility, setTimechainView, setNodeMapActive, onResetCamera]);
  const handleMute = useCallback(() => { setIsMuted(m => { setMuted(!m); return !m; }); }, [setIsMuted]);

  const [satsTimeLeft, setSatsTimeLeft] = useState(30);
  const [satsFinalScore, setSatsFinalScore] = useState(null);
  const satsScoreRef = useRef(satsScore);
  satsScoreRef.current = satsScore;

  const [installPrompt, setInstallPrompt] = useState(null);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
  const { isOpen: isInstallOpen, onOpen: onInstallOpen, onClose: onInstallClose } = useDisclosure();
  const { isOpen: isWpOpen, onOpen: onWpOpen, onClose: onWpClose } = useDisclosure();
  const { isOpen: isAchOpen, onOpen: onAchOpen, onClose: onAchClose } = useDisclosure();
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure();
  const { isOpen: isFeeMapOpen, onOpen: onFeeMapOpen, onClose: onFeeMapClose } = useDisclosure();
  const { isOpen: isNotifHelpOpen, onOpen: onNotifHelpOpen, onClose: onNotifHelpClose } = useDisclosure();
  const { isOpen: isTourOpen, onOpen: onTourOpen, onClose: onTourCloseRaw } = useDisclosure();

  useEffect(() => {
    if (!ENABLE_TOUR) return;
    if (typeof window === 'undefined') return;
    try {
      if (!localStorage.getItem(ONBOARDING_SEEN_KEY)) {
        const timer = setTimeout(() => onTourOpen(), 1200);
        return () => clearTimeout(timer);
      }
    } catch (e) {}
  }, [onTourOpen]);

  const completeTour = useCallback(() => {
    onTourCloseRaw();
    try { localStorage.setItem(ONBOARDING_SEEN_KEY, '1'); } catch (e) {}
  }, [onTourCloseRaw]);

  const replayTour = useCallback(() => {
    onTourOpen();
    setTimeout(() => onClose(), 250);
  }, [onClose, onTourOpen]);
  const [searchInitialQuery, setSearchInitialQuery] = useState(null);
  const notif = useBlockNotifications(block, isNewBlock, ENABLE_BLOCK_NOTIFICATIONS);

  const handleNotifClick = useCallback(() => {
    if (notif.permission === 'denied' || notif.dismissedPrompt) {
      onNotifHelpOpen();
      return;
    }
    notif.toggle();
  }, [notif, onNotifHelpOpen]);

  useEffect(() => {
    if (!ENABLE_DEEP_LINK_SEARCH) return;
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const deepLink = params.get('block');
    if (deepLink) {
      setSearchInitialQuery(deepLink);
      onSearchOpen();
      params.delete('block');
      const query = params.toString();
      const url = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
      window.history.replaceState({}, '', url);
    }
  }, [onSearchOpen]);

  const handleSearchClose = useCallback(() => {
    onSearchClose();
    setSearchInitialQuery(null);
  }, [onSearchClose]);
  const { achievements, unlock, pending, clearPending, count: achCount, allComplete } = useAchievements();
  const [achNotification, setAchNotification] = useState(null);

  // Achievement unlock overlay + mega celebration on completion
  useEffect(() => {
    if (!pending) return;
    setAchNotification(pending);
    clearPending();
    if (pending.allComplete) {
      // Mega celebration: fireworks + konami explosion
      if (!isMuted) playMilestoneSound();
      setMilestone(true);
      setTimeout(() => setMilestone(false), 4000);
      setKonami(true);
      setTimeout(() => setKonami(false), 10000);
    }
    const timer = setTimeout(() => setAchNotification(null), pending.allComplete ? 8000 : 4000);
    return () => clearTimeout(timer);
  }, [pending, clearPending, setMilestone, setKonami, isMuted]);

  // Unlock insideCube achievement
  useEffect(() => {
    if (isInsideCube) unlock('insideCube');
  }, [isInsideCube, unlock]);

  // Wire camera achievement callback from TesseractScene
  useEffect(() => {
    if (cameraAchievementRef) cameraAchievementRef.current = (id) => unlock(id);
  }, [cameraAchievementRef, unlock]);

  // Track currency switch and unmute
  const initialCurrencyRef = useRef(currency);
  useEffect(() => {
    if (currency !== initialCurrencyRef.current) unlock('currency');
  }, [currency, unlock]);
  useEffect(() => {
    if (!isMuted) unlock('unmute');
  }, [isMuted, unlock]);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const [showInstallBanner, setShowInstallBanner] = useState(false);
  useEffect(() => {
    if (!installPrompt) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;
    if (localStorage.getItem('installBannerDismissed') === '1') return;
    const timer = setTimeout(() => setShowInstallBanner(true), 30_000);
    return () => clearTimeout(timer);
  }, [installPrompt]);

  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    try { localStorage.setItem('installBannerDismissed', '1'); } catch (e) {}
  }, []);

  const acceptInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => {
        setInstallPrompt(null);
        try { localStorage.setItem('installBannerDismissed', '1'); } catch (e) {}
      });
    }
  }, [installPrompt]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.code === 'Escape') {
        if (explorerBlock || explorerTx || searchedBlock || genesisActive) {
          setExplorerBlock(null);
          setExplorerTx(null);
          setSearchedBlock(null);
          setGenesisActive(false);
          onResetCamera?.();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [explorerBlock, explorerTx, setExplorerBlock, setExplorerTx, onResetCamera, searchedBlock, setSearchedBlock, genesisActive, setGenesisActive]);

  useEffect(() => {
    if (!satsGame) {
      setSatsTimeLeft(30);
      return;
    }
    const interval = setInterval(() => {
      setSatsTimeLeft(t => {
        if (t <= 1) {
          setSatsGame(false);
          setSatsFinalScore(satsScoreRef.current);
          setTimeout(() => setSatsFinalScore(null), 4000);
          return 30;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [satsGame, setSatsGame]);

  useEffect(() => {
    if (satsPickups.length === 0) return;
    const timer = setTimeout(() => {
      setSatsPickups(prev => prev.slice(1));
    }, 900);
    return () => clearTimeout(timer);
  }, [satsPickups, setSatsPickups]);

  const handleInstall = useCallback(() => {
    if (installPrompt) {
      installPrompt.prompt();
      installPrompt.userChoice.then(() => setInstallPrompt(null));
    }
    onInstallOpen();
  }, [installPrompt, onInstallOpen]);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  }, []);

  const rate = fxRates[currency] || 1;
  const cPrice = convert(price, rate);

  const priceDirection = price && stats?.open
    ? price > stats.open ? 'up' : price < stats.open ? 'down' : 'flat'
    : 'flat';
  // In sats mode, direction flips: price up = fewer sats per unit = bearish for stackers
  const direction = satsMode
    ? (priceDirection === 'up' ? 'down' : priceDirection === 'down' ? 'up' : 'flat')
    : priceDirection;

  const accent = direction === 'up' ? GREEN : direction === 'down' ? RED : ORANGE;
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '—';
  const pct = price && stats?.open
    ? (((price - stats.open) / stats.open) * 100).toFixed(satsMode ? 1 : 2)
    : null;

  const currencySymbol = (CURRENCIES.find(c => c.code === currency) || CURRENCIES[0]).symbol;
  const satsPerUnit = cPrice ? Math.floor(100_000_000 / cPrice) : null;
  const isMobile = useBreakpointValue({ base: true, md: false });
  const activeSpeed = Math.round(pulseSpeed * 10);

  const HALVING_INTERVAL = 210_000;
  const halvingBlocks = block?.height ? NEXT_HALVING - block.height : null;
  const halvingProgress = block?.height ? ((block.height % HALVING_INTERVAL) / HALVING_INTERVAL) * 100 : 0;

  const minedSupply = block?.height ? getMinedSupply(block.height) : 0;
  const inflationRate = block?.height ? getInflationRate(block.height) : 0;
  const supplyPercent = (minedSupply / 21_000_000) * 100;

  return (
    <Box position="absolute" inset={0} pointerEvents="none" zIndex={10}>

      <Crosshair />

      {}
      <Flex
        position="absolute" top={0} left={0} right={0}
        px={{ base: 3, md: 5 }} py={{ base: 2, md: 3 }}
        justify="space-between" align="center"
        pointerEvents="none"
      >
        <HStack
          spacing="6px" align="center" pointerEvents="auto"
          cursor="pointer" onClick={(e) => {
            if (onLogoTap) onLogoTap();
            handleReset();
          }}
          _hover={{ opacity: 0.8 }} transition="opacity 0.2s"
          title="Reset view"
        >
          <Text
            fontSize={{ base: '11px', md: '13px' }} fontWeight="700"
            letterSpacing="0.4em" color="whiteAlpha.800"
            textShadow="0 0 10px rgba(255,255,255,0.3)"
            fontFamily="mono" textTransform="uppercase"
          >
            TESSERACT
          </Text>
          <Box
            as="svg" viewBox="0 0 273.6 360" w={{ base: '12px', md: '14px' }} h={{ base: '16px', md: '18px' }}
            fill={ORANGE} filter={`drop-shadow(0 0 4px ${ORANGE}88)`}
          >
            <path d="M217.021,167.042c18.631-9.483,30.288-26.184,27.565-54.007c-3.667-38.023-36.526-50.773-78.006-54.404l-0.008-52.741h-32.139l-0.009,51.354c-8.456,0-17.076,0.166-25.657,0.338L108.76,5.897l-32.11-0.003l-0.006,52.728c-6.959,0.142-13.793,0.277-20.466,0.277v-0.156l-44.33-0.018l0.006,34.282c0,0,23.734-0.446,23.343-0.013c13.013,0.009,17.262,7.559,18.484,14.076l0.01,60.083v84.397c-0.573,4.09-2.984,10.625-12.083,10.637c0.414,0.364-23.379-0.004-23.379-0.004l-6.375,38.335h41.817c7.792,0.009,15.448,0.13,22.959,0.19l0.028,53.338l32.102,0.009l-0.009-52.779c8.832,0.18,17.357,0.258,25.684,0.247l-0.009,52.532h32.138l0.018-53.249c54.022-3.1,91.842-16.697,96.544-67.385C266.916,192.612,247.692,174.396,217.021,167.042z M109.535,95.321c18.126,0,75.132-5.767,75.14,32.064c-0.008,36.269-56.996,32.032-75.14,32.032V95.321z M109.521,262.447l0.014-70.672c21.778-0.006,90.085-6.261,90.094,35.32C199.638,266.971,131.313,262.431,109.521,262.447z" />
          </Box>
        </HStack>

        <HStack spacing={3} align="center" pointerEvents="auto">
          <Text fontSize="9px" color="whiteAlpha.300" fontFamily="mono" letterSpacing="0.15em">
            BTC/
          </Text>
          <CurrencySelector currency={currency} setCurrency={setCurrency} />
        </HStack>
      </Flex>

      {}
      {isNewBlock && (
        <Flex
          position="absolute" inset={0} align="flex-start" justify="center" pt="80px" zIndex={20}
          animation="blockAlert 3.5s ease-out forwards"
          sx={{
            '@keyframes blockAlert': {
              '0%': { opacity: 0 },
              '5%': { opacity: 1 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0 },
            },
          }}
        >
          <VStack spacing={1}>
            <Text
              fontSize="9px" fontFamily="mono" letterSpacing="0.5em"
              color={ORANGE_DIM} fontWeight="500"
            >
              NEW BLOCK DETECTED
            </Text>
            <Text
              fontSize={{ base: '28px', md: '42px' }} fontWeight="700"
              fontFamily="mono" letterSpacing="0.15em" color={ORANGE}
              sx={{
                textShadow: `0 0 60px ${ORANGE}66, 0 0 120px ${ORANGE}22`,
              }}
            >
              {block?.height ? fmtNum(block.height) : ''}
            </Text>
            <Box w="60px" h="1px" bg={ORANGE_DIM} />
          </VStack>
        </Flex>
      )}

      {}
      {konamiSlogan && (
        <Flex
          position="absolute" top="60px" left="50%" transform="translateX(-50%)"
          zIndex={20} pointerEvents="none"
          key={konamiSlogan}
          animation="sloganFade 1.2s ease-out forwards"
          sx={{
            '@keyframes sloganFade': {
              '0%': { opacity: 0, transform: 'translateX(-50%) scale(0.9)' },
              '15%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
              '100%': { opacity: 1, transform: 'translateX(-50%) scale(1)' },
            },
          }}
        >
          <Text
            fontSize={{ base: '16px', md: '24px' }} fontWeight="700"
            fontFamily="mono" letterSpacing="0.2em" color={ORANGE}
            textAlign="center" whiteSpace="nowrap"
            textShadow={`0 0 40px ${ORANGE}66, 0 0 80px ${ORANGE}22`}
          >
            {konamiSlogan}
          </Text>
        </Flex>
      )}

      {}
      <Box
        position="absolute"
        top={{ base: '40px', md: '55px' }}
        left={{ base: 2, md: 4 }}
        w={{ base: '155px', md: '195px' }}
      >
        <Flex justify="flex-start" mb="6px">
          <HudToggle
            iconKey="price"
            visible={hudVisibility.price}
            onClick={() => toggleHud('price')}
            title={hudVisibility.price ? 'Hide price panel' : 'Show price panel'}
          />
        </Flex>
        {hudVisibility.price && (
        <BracketPanel accentColor={accent + '66'}>
          <VStack spacing="6px" align="stretch">
            <TLabel color={accent} tooltip={satsMode
              ? `Satoshis per ${currencySymbol}1 — 100,000,000 sats = 1 BTC. Click the row below to switch back to fiat.`
              : 'Live BTC price from Coinbase, with the 24h change below. Click the SATS row to flip to sats-per-fiat.'}>{satsMode ? `SATS / ${currencySymbol}1` : 'PRICE'}</TLabel>

            <HStack spacing={2} align="baseline">
              <TVal color={accent} large pulse={!loading}
                textShadow={`0 0 16px ${accent}88, 0 0 30px ${accent}44`}>
                {loading ? '---' : satsMode
                  ? (satsPerUnit ? satsPerUnit.toLocaleString() + ' sats' : '---')
                  : fmtCurrency(cPrice, currency)}
              </TVal>
            </HStack>

            {!loading && (
              <HStack spacing={2} align="baseline">
                <Text fontSize="11px" color={accent} fontFamily="mono" fontWeight="600"
                  textShadow={`0 0 8px ${accent}66`}>
                  {arrow}
                </Text>
                {pct !== null && (
                  <Text fontSize="11px" color={accent} fontFamily="mono" fontWeight="500"
                    letterSpacing="0.05em" opacity={0.8}>
                    {pct > 0 ? '+' : ''}{pct}%
                  </Text>
                )}
              </HStack>
            )}

            {stats && (() => {
              const toSats = (usdPrice) => {
                const converted = convert(usdPrice, rate);
                return converted ? Math.floor(100_000_000 / converted).toLocaleString() : '---';
              };
              const fmtVal = (v) => satsMode ? toSats(v) : fmtCurrency(convert(v, rate), currency);
              return (
                <>
                  <Rule color="whiteAlpha.100" />
                  <TRow label="open" value={fmtVal(stats.open)} />
                  <TRow label={satsMode ? 'low' : 'high'} value={fmtVal(stats.high)} color={satsMode ? RED + '99' : GREEN + '99'} />
                  <TRow label={satsMode ? 'high' : 'low'} value={fmtVal(stats.low)} color={satsMode ? GREEN + '99' : RED + '99'} />
                  {!isMobile && <TRow label="vol" value={fmtVol(stats.volume) + ' BTC'} />}
                  <Rule color="whiteAlpha.100" />
                  <Flex
                    justify="space-between" align="baseline" gap={2}
                    cursor="pointer" _hover={{ opacity: 0.8 }} transition="opacity 0.2s"
                    onClick={() => setSatsMode(s => !s)}
                    pointerEvents="auto"
                    title="Toggle sats mode"
                  >
                    <TLabel color={satsMode ? ORANGE : 'whiteAlpha.800'}>sats/{currencySymbol}</TLabel>
                    <Box flex={1} borderBottom="1px dotted" borderColor="whiteAlpha.300" mb="3px" mx={1} />
                    <TVal color={satsMode ? ORANGE : 'white'}>
                      {satsPerUnit ? satsPerUnit.toLocaleString() : '---'}
                    </TVal>
                  </Flex>
                </>
              );
            })()}
          </VStack>
        </BracketPanel>
        )}

        {hudVisibility.price && !!ENABLE_NODE_GLOBE && nodeMapActive && nodeCount != null && (
          <BracketPanel accentColor="#BB77FF66" mt={2}>
            <Flex justify="space-between" align="baseline">
              <TLabel color="#BB77FF" tooltip="Reachable Bitcoin nodes currently advertised on the network (source: Bitnodes).">NODES</TLabel>
              <TVal fontSize="12px" color="#BB77FF" textShadow="0 0 6px #BB77FF44">
                {fmtNum(nodeCount)}
              </TVal>
            </Flex>
          </BracketPanel>
        )}

        {/* Reachable-node breakdown — anchors below the NODES count */}
        <NodeCountriesPanel />

        {achNotification && (() => {
          const isGold = achNotification.allComplete;
          const ac = isGold ? GOLD : ORANGE;
          return (
            <Box
              mt={3}
              w={{ base: '220px', md: '280px' }}
              key={achNotification.id}
              animation="achUnlock 5s ease-out forwards"
              sx={{
                '@keyframes achUnlock': {
                  '0%': { opacity: 0, transform: 'scale(0.9) translateY(10px)' },
                  '6%': { opacity: 1, transform: 'scale(1.03) translateY(0)' },
                  '10%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                  '75%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                  '100%': { opacity: 0, transform: 'scale(0.97) translateY(-6px)' },
                },
              }}
            >
              <BracketPanel
                accentColor={ac}
                bg={`rgba(0, 0, 0, 0.6)`}
                border="1px solid"
                borderColor={ac + '44'}
                boxShadow={`0 0 30px ${ac}33, inset 0 0 20px ${ac}11`}
                p={{ base: '12px', md: '16px' }}
              >
                <VStack spacing={2} align="stretch">
                  <HStack spacing={3} align="center">
                    <Flex
                      w="36px" h="36px" minW="36px"
                      align="center" justify="center"
                      border="1px solid" borderColor={ac + '66'}
                      bg={ac + '11'}
                      animation="achIconPulse 2s ease-in-out infinite"
                      sx={{
                        '@keyframes achIconPulse': {
                          '0%, 100%': { boxShadow: `0 0 8px ${ac}22` },
                          '50%': { boxShadow: `0 0 16px ${ac}55` },
                        },
                      }}
                    >
                      <Box as="svg" viewBox="0 0 24 24" w="18px" h="18px" fill={ac}>
                        <path d="M5 3h14v2h-1v1a5 5 0 01-2.1 4.1A3.5 3.5 0 0113.5 13H13v3h2v2h1v2H8v-2h1v-2h2v-3h-.5a3.5 3.5 0 01-2.4-2.9A5 5 0 016 6V5H5V3zm3 2v1a3 3 0 002 2.83V9a1.5 1.5 0 001.5 1.5h1A1.5 1.5 0 0014 9v-.17A3 3 0 0016 6V5H8z"/>
                      </Box>
                    </Flex>
                    <VStack spacing={0} align="stretch" flex={1}>
                      <Text fontSize="10px" fontFamily="mono" fontWeight="600" letterSpacing="0.4em"
                        color={ac} lineHeight="1.4">
                        {isGold ? 'FULL NODE' : 'ACHIEVEMENT UNLOCKED'}
                      </Text>
                      <Text fontSize={{ base: '17px', md: '20px' }} fontFamily="mono" fontWeight="700" letterSpacing="0.12em"
                        color="white" lineHeight="1.2"
                        textShadow={`0 0 20px ${ac}66, 0 0 40px ${ac}33`}>
                        {achNotification.name}
                      </Text>
                    </VStack>
                  </HStack>
                  <Box>
                    <Flex justify="space-between" mb="3px">
                      <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.700" letterSpacing="0.15em">
                        DISCOVERED
                      </Text>
                      <Text fontSize="10px" fontFamily="mono" color={ac} letterSpacing="0.1em" fontWeight="700">
                        {achNotification.count} / {ACHIEVEMENTS.length}
                      </Text>
                    </Flex>
                    <Box w="100%" h="3px" bg="whiteAlpha.100" overflow="hidden">
                      <Box h="100%" w={`${(achNotification.count / ACHIEVEMENTS.length) * 100}%`}
                        bg={ac} boxShadow={`0 0 8px ${ac}66`} transition="width 0.5s ease" />
                    </Box>
                  </Box>
                </VStack>
              </BracketPanel>
            </Box>
          );
        })()}
      </Box>

      {}
      <Box
        position="absolute"
        top={{ base: '40px', md: '55px' }}
        right={{ base: 2, md: 4 }}
        w={{ base: '155px', md: '195px' }}
      >
        <Flex justify="flex-end" mb="6px">
          <HudToggle
            iconKey="block"
            visible={hudVisibility.block}
            onClick={() => toggleHud('block')}
            title={hudVisibility.block ? 'Hide block panel' : 'Show block panel'}
          />
        </Flex>
        {hudVisibility.block && (
        <BracketPanel accentColor={ORANGE_DIM}>
          <VStack spacing="6px" align="stretch">

            <TLabel color={ORANGE} tooltip="The most recently mined Bitcoin block. A new one is found about every 10 minutes.">LATEST</TLabel>

            {blockLoading || !block ? (
              <TVal color="whiteAlpha.300">AWAITING DATA</TVal>
            ) : (
              <>
                <TVal color={ORANGE} large fontWeight="700"
                  textShadow={`0 0 16px ${ORANGE}88, 0 0 30px ${ORANGE}44`}>
                  {fmtNum(block.height)}
                </TVal>
                <Rule color="whiteAlpha.100" />
                <TRow label="miner" value={block.pool} tooltip="Mining pool that found this block." />
                <TRow label="txs" value={fmtNum(block.txCount)} tooltip="Number of transactions confirmed in this block." />
                {!isMobile && <TRow label="size" value={fmtSize(block.size)} tooltip="Block size on disk. Bitcoin blocks are capped at ~4 MB of weight (SegWit)." />}
                <TRow label="fees" value={fmtSats(block.totalFees) + ' BTC'} tooltip="Total transaction fees paid to the miner of this block (excludes the subsidy)." />
                {!isMobile && (
                  <TRow label="rate" value={fmtFeeRate(block.medianFee)} tooltip="Median fee rate of transactions in this block, in satoshis per virtual byte." />
                )}
                <TimeSinceBlock timestamp={block.timestamp} />
              </>
            )}

            {fees && !isMobile && (
              <>
                <Rule label="fee est" tooltip="Recommended fee rates by confirmation target. Live from mempool.space." />
                <TRow label="next" value={fees.fastestFee + ' s/vB'} color="#ffcc00" tooltip="Pay this fee rate to likely confirm in the very next block (~10 min)." />
                <TRow label="30m" value={fees.halfHourFee + ' s/vB'} tooltip="Likely to confirm within roughly 30 minutes (a few blocks)." />
                <TRow label="1h" value={fees.hourFee + ' s/vB'} tooltip="Likely to confirm within roughly an hour." />
              </>
            )}

            {mempoolTxCount != null && (
              <>
                <Rule label="mempool" tooltip="The mempool is the queue of pending, unconfirmed transactions waiting to be mined." />
                <TRow label="txs" value={fmtNum(mempoolTxCount)} color="#BB77FF" tooltip="Total unconfirmed transactions currently waiting in the mempool." />
                {vBytesPerSecond != null && (
                  <TRow label="intake" value={fmtVbps(vBytesPerSecond)} tooltip="Rate at which new transaction data is entering the mempool (virtual bytes per second)." />
                )}
              </>
            )}

            {blockHistory.length > 0 && (
              <>
                <Rule label="history" tooltip="Recently mined blocks, newest first. Click any row to fly into it — same as clicking the orbiting cube." />
                <VStack
                  spacing="3px"
                  align="stretch"
                  maxH={{ base: '180px', md: '240px' }}
                  overflowY="auto"
                  pr="2px"
                  sx={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: ORANGE_DIM, borderRadius: '2px' },
                  }}
                >
                  {blockHistory.map((b, i) => (
                    <BlockListRow
                      key={b.height}
                      block={b}
                      isActive={explorerBlock?.height === b.height}
                      isHovered={hoveredBlockIdx === i}
                      onClick={() => {
                        if (genesisActive) setGenesisActive(false);
                        if (searchedBlock) setSearchedBlock(null);
                        setExplorerBlock(b);
                      }}
                      onHoverEnter={() => setHoveredBlockIdx(i)}
                      onHoverLeave={() => setHoveredBlockIdx(null)}
                    />
                  ))}
                </VStack>
              </>
            )}
          </VStack>
        </BracketPanel>
        )}

        {}
        {hudVisibility.block && !explorerBlock && (() => {
          const otherBlock = hoveredCubeBlock || (hoveredBlockIdx !== null ? blockHistory[hoveredBlockIdx] : null);
          if (genesisActive && otherBlock) setGenesisActive(false);
          const hb = otherBlock
            || (searchedBlock ? searchedBlock : null)
            || (genesisActive ? GENESIS_BLOCK : null);
          if (!hb) return null;
          const mode = otherBlock ? 'block' : searchedBlock ? 'searched' : 'genesis';
          const accent = mode === 'genesis' ? GOLD : mode === 'searched' ? RED_ORANGE : ORANGE;
          const label = mode === 'genesis' ? 'GENESIS' : mode === 'searched' ? 'LAST SEARCHED' : 'BLOCK';
          const esploraLimited = mode === 'searched' && hb.source === 'esplora';
          return (
            <Box
              position="absolute"
              top={{ base: '100%', md: 'auto' }}
              bottom={{ base: 'auto', md: 0 }}
              right={{ base: 0, md: '100%' }}
              mt={{ base: 1, md: 0 }}
              mr={{ base: 0, md: 2 }}
              w={{ base: '155px', md: '195px' }}
              pointerEvents="none"
              zIndex={30}
            >
              <BracketPanel accentColor={accent}>
                <VStack spacing="5px" align="stretch">
                  <Flex justify="space-between" align="baseline" gap={2}>
                    <TLabel color={accent}>{label}</TLabel>
                    <Text fontSize="14px" fontFamily="mono" fontWeight="700" color={accent} lineHeight="1">
                      {fmtNum(hb.height)}
                    </Text>
                  </Flex>
                  {hb.pool != null && <TRow label="miner" value={hb.pool} />}
                  <TRow label="txs" value={fmtNum(hb.txCount)} />
                  <TRow label="size" value={fmtSize(hb.size)} />
                  {hb.totalFees != null && <TRow label="fees" value={fmtSats(hb.totalFees) + ' BTC'} />}
                  {hb.medianFee != null && <TRow label="rate" value={fmtFeeRate(hb.medianFee)} />}
                  <TimeSinceBlock timestamp={hb.timestamp} />
                  {esploraLimited && (
                    <Text fontSize="8px" color="whiteAlpha.600" fontFamily="mono" fontStyle="italic" mt={1}>
                      limited data (esplora)
                    </Text>
                  )}
                </VStack>
              </BracketPanel>
            </Box>
          );
        })()}
      </Box>

      {}
      <Box
        position="absolute"
        bottom={{ base: '160px', md: 4 }}
        left={{ base: 2, md: 4 }}
        pointerEvents="auto"
      >
        <VStack spacing={2} align="flex-start">
          {welcomeVisible && (
            <Box mb={{ base: '12px', md: '24px' }}>
              <EditableWelcome />
            </Box>
          )}
          <HStack spacing={2} align="center">
            <Box
              as="button"
              w="22px" h="22px"
              display="flex" alignItems="center" justifyContent="center"
              color={welcomeVisible ? ORANGE : 'whiteAlpha.600'}
              cursor="pointer"
              border="1px solid"
              borderColor={welcomeVisible ? ORANGE_DIM : 'whiteAlpha.300'}
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={() => setWelcomeVisible(v => toggleWelcomeWithDebounce(v))}
              title={welcomeVisible ? 'Hide welcome message (W)' : 'Show welcome message (W)'}
              aria-label="Toggle welcome message"
            >
              <Box as="svg" viewBox="0 0 24 24" w="12px" h="12px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </Box>
            </Box>
            <TLabel tooltip="Tesseract rotation speed. Click bars 1–5 (1 = slow, 5 = fast).">SPD</TLabel>
            <SpeedBar active={activeSpeed} onChange={(n) => { setPulseSpeed(+(n * 0.1).toFixed(1)); if (n === 5) unlock('speedMax'); }} />
          </HStack>
          <HStack spacing={1}>
            <CtrlBtn onClick={() => { setIsPaused(p => !p); unlock('pause'); }} active={isPaused}>
              {isPaused ? '▶ PLAY' : '‖ PAUSE'}
            </CtrlBtn>
            {}
            {false && <CtrlBtn onClick={() => { handleMute(); if (isMuted) unlock('unmute'); }}>{isMuted ? 'UNMUTE' : 'MUTE'}</CtrlBtn>}
            <CtrlBtn onClick={() => { handleFullscreen(); unlock('fullscreen'); }}>FULLSCREEN</CtrlBtn>
            <CtrlBtn onClick={() => { handleReset(); unlock('resetView'); }}>RESET</CtrlBtn>
            {!isStandalone && <CtrlBtn onClick={handleInstall}>GET APP</CtrlBtn>}
            {false && <CtrlBtn onClick={() => setNewBlockTrigger(n => n + 1)}>TEST</CtrlBtn>}
          </HStack>
          <HStack spacing={1}>
            <CtrlBtn as="a" href="https://exploremempool.com" target="_blank" rel="noopener noreferrer">
              EXPLORE MEMPOOL
            </CtrlBtn>
            <CtrlBtn as="a" href="https://timechainstats.com" target="_blank" rel="noopener noreferrer">
              TIMECHAIN STATS
            </CtrlBtn>
          </HStack>
        </VStack>
      </Box>

      {}
      <Box
        position="absolute"
        bottom={{ base: '160px', md: 4 }}
        right={{ base: 2, md: 4 }}
        pointerEvents="auto"
      >
        <VStack spacing={2} align="flex-end">
          {hudVisibility.network && (difficulty || halvingBlocks) && (
            <BracketPanel accentColor="whiteAlpha.300" p="10px">
              <VStack spacing="5px" align="stretch" minW="160px">
                {difficulty && (() => {
                  const diffColor = difficulty.difficultyChange >= 0 ? GREEN : RED;
                  const diffGlow = Math.min(difficulty.progressPercent / 100, 1);
                  return (
                    <>
                      <Flex justify="space-between" align="baseline">
                        <TLabel tooltip="Mining difficulty re-targets every 2,016 blocks (~2 weeks). The bar shows progress toward the next adjustment; the percent at right is the projected change.">DIFFICULTY</TLabel>
                        <TVal fontSize="10px" color={diffColor}
                          textShadow={`0 0 6px ${diffColor}66`}>
                          <Text as="span" color="whiteAlpha.600">EST</Text> {difficulty.difficultyChange >= 0 ? '+' : ''}{parseFloat(difficulty.difficultyChange?.toFixed(1))}%
                        </TVal>
                      </Flex>
                      <Box h="3px" bg="whiteAlpha.200" position="relative" borderRadius="1px">
                        <Box
                          position="absolute" top={0} left={0} h="100%"
                          w={`${difficulty.progressPercent}%`}
                          bg={diffColor}
                          opacity={0.7}
                          transition="width 1s ease"
                          borderRadius="1px"
                          boxShadow={`0 0 ${4 + diffGlow * 12}px ${diffColor}${Math.round(diffGlow * 200).toString(16).padStart(2, '0')}`}
                          animation={diffGlow > 0.7 ? 'barPulse 2s ease-in-out infinite' : undefined}
                          sx={diffGlow > 0.7 ? {
                            '@keyframes barPulse': {
                              '0%, 100%': { opacity: 0.7 },
                              '50%': { opacity: 1, boxShadow: `0 0 ${8 + diffGlow * 20}px ${diffColor}cc` },
                            },
                          } : undefined}
                        />
                      </Box>
                      <Flex justify="space-between">
                        <TVal fontSize="10px" color={diffColor}
                          textShadow={`0 0 6px ${diffColor}66`}>
                          {parseFloat(difficulty.progressPercent?.toFixed(1))}%
                        </TVal>
                        <TVal fontSize="10px">
                          {fmtNum(difficulty.remainingBlocks)} BLKS LEFT
                        </TVal>
                      </Flex>
                    </>
                  );
                })()}

                {halvingBlocks !== null && halvingBlocks > 0 && (() => {
                  const halvGlow = halvingProgress / 100;
                  const totalMins = halvingBlocks * 10;
                  const yrs = Math.floor(totalMins / (60 * 24 * 365));
                  const days = Math.floor((totalMins % (60 * 24 * 365)) / (60 * 24));
                  const hrs = Math.floor((totalMins % (60 * 24)) / 60);
                  const mins = Math.round(totalMins % 60);
                  const timeStr = yrs > 0 ? `${yrs}y ${days}d`
                    : days > 0 ? `${days}d ${hrs}h`
                    : hrs > 0 ? `${hrs}h ${mins}m`
                    : `${mins}m`;
                  const halvingEra = Math.floor(NEXT_HALVING / HALVING_INTERVAL);
                  const currentReward = 50 / Math.pow(2, halvingEra - 1);
                  const nextReward = currentReward / 2;
                  return (
                    <>
                      <Rule color="whiteAlpha.300" />
                      <Flex justify="space-between" align="baseline">
                        <TLabel color={ORANGE} tooltip={
                          <>
                            Every <Text as="span" color={ORANGE} fontWeight="700">210,000 blocks</Text> (~4 years), the mining reward cuts in half. Block <Text as="span" color={ORANGE} fontWeight="700">{fmtNum(NEXT_HALVING)}</Text> drops the reward from <Text as="span" color={ORANGE} fontWeight="700">{currentReward} BTC</Text> to <Text as="span" color={ORANGE} fontWeight="700">{nextReward} BTC</Text> per block.
                          </>
                        }>HALVING</TLabel>
                        <TVal fontSize="10px" color={ORANGE}
                          textShadow={`0 0 6px ${ORANGE}66`}>
                          <Text as="span" color="whiteAlpha.600">LEFT</Text> ~{timeStr}
                        </TVal>
                      </Flex>
                      <Box h="3px" bg="whiteAlpha.200" position="relative" borderRadius="1px">
                        <Box
                          position="absolute" top={0} left={0} h="100%"
                          w={`${halvingProgress}%`}
                          bg={ORANGE}
                          opacity={0.8}
                          transition="width 1s ease"
                          borderRadius="1px"
                          boxShadow={`0 0 ${4 + halvGlow * 12}px ${ORANGE}${Math.round(halvGlow * 200).toString(16).padStart(2, '0')}`}
                          animation={halvGlow > 0.5 ? 'halvPulse 3s ease-in-out infinite' : undefined}
                          sx={halvGlow > 0.5 ? {
                            '@keyframes halvPulse': {
                              '0%, 100%': { opacity: 0.8 },
                              '50%': { opacity: 1, boxShadow: `0 0 ${8 + halvGlow * 20}px ${ORANGE}cc` },
                            },
                          } : undefined}
                        />
                      </Box>
                      <Flex justify="space-between">
                        <TVal fontSize="10px" color={ORANGE}
                          textShadow={`0 0 6px ${ORANGE}66`}>
                          {parseFloat(halvingProgress.toFixed(1))}%
                        </TVal>
                        <TVal fontSize="10px">
                          {fmtNum(halvingBlocks)} BLKS LEFT
                        </TVal>
                      </Flex>
                    </>
                  );
                })()}

                {minedSupply > 0 && (() => {
                  const supplyGlow = supplyPercent / 100;
                  const PURPLE = '#BB77FF';
                  return (
                    <>
                      <Rule color="whiteAlpha.300" />
                      <Flex justify="space-between" align="baseline">
                        <TLabel color={PURPLE} tooltip="Total BTC mined so far out of the 21M cap. The bar shows progress toward 21M; the small number at right is the current annual issuance rate.">SUPPLY</TLabel>
                        <TVal fontSize="10px">
                          {(minedSupply / 1_000_000).toFixed(2)}M <Text as="span" color="whiteAlpha.600">/ 21M</Text>
                        </TVal>
                      </Flex>
                      <Box h="3px" bg="whiteAlpha.200" position="relative" borderRadius="1px">
                        <Box
                          position="absolute" top={0} left={0} h="100%"
                          w={`${supplyPercent}%`}
                          bg={PURPLE}
                          opacity={0.7}
                          transition="width 1s ease"
                          borderRadius="1px"
                          boxShadow={`0 0 ${4 + supplyGlow * 8}px ${PURPLE}${Math.round(supplyGlow * 150).toString(16).padStart(2, '0')}`}
                        />
                      </Box>
                      <Flex justify="space-between">
                        <TVal fontSize="10px" color={PURPLE}
                          textShadow={`0 0 6px ${PURPLE}44`}>
                          {supplyPercent.toFixed(2)}%
                        </TVal>
                        <TVal fontSize="10px" color="whiteAlpha.600">
                          {inflationRate.toFixed(2)}% / yr
                        </TVal>
                      </Flex>
                    </>
                  );
                })()}
              </VStack>
            </BracketPanel>
          )}

          {}
          <HStack spacing={2} align="center">
            <Text fontSize="9px" fontFamily="mono" color="whiteAlpha.600" letterSpacing="0.1em">
              v{pkg.version}
            </Text>
            <Box
              as="button"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              color="whiteAlpha.600" cursor="pointer"
              border="1px solid" borderColor="whiteAlpha.300"
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={onSearchOpen}
              title="Block search"
            >
              <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
              </Box>
            </Box>
            <Box
              as="button"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              color="whiteAlpha.600" cursor="pointer"
              border="1px solid" borderColor="whiteAlpha.300"
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={onFeeMapOpen}
              title="Fee heatmap — projected blocks"
            >
              <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="12" width="4" height="9" />
                <rect x="10" y="7" width="4" height="14" />
                <rect x="17" y="3" width="4" height="18" />
              </Box>
            </Box>
            {notif.supported && (
              <Box
                as="button"
                w="28px" h="28px"
                display="flex" alignItems="center" justifyContent="center"
                color={notif.enabled ? ORANGE : 'whiteAlpha.600'}
                cursor="pointer"
                border="1px solid"
                borderColor={notif.enabled ? ORANGE : 'whiteAlpha.300'}
                transition="all 0.2s"
                _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
                onClick={handleNotifClick}
                title={notif.permission === 'denied'
                  ? 'Notifications blocked — click for instructions'
                  : notif.dismissedPrompt
                    ? 'Prompt dismissed — click for instructions'
                    : notif.enabled
                      ? 'Block alerts on — click to disable'
                      : 'Enable browser alerts for new blocks'}
                opacity={(notif.permission === 'denied' || notif.dismissedPrompt) ? 0.55 : 1}
              >
                {notif.enabled ? (
                  <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="currentColor">
                    <path d="M12 2a1.5 1.5 0 00-1.5 1.5v.75A6 6 0 006 10v5l-1.6 1.6a.75.75 0 00.53 1.28h14.14a.75.75 0 00.53-1.28L18 15v-5a6 6 0 00-4.5-5.75V3.5A1.5 1.5 0 0012 2zm0 20a2.5 2.5 0 002.45-2h-4.9A2.5 2.5 0 0012 22z" />
                  </Box>
                ) : (
                  <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </Box>
                )}
              </Box>
            )}
            <Box
              as="button"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="11px" fontWeight="700" fontFamily="mono"
              color="whiteAlpha.600" cursor="pointer"
              border="1px solid" borderColor="whiteAlpha.300"
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={onOpen}
            >
              ?
            </Box>
            <Box
              as="button"
              position="relative"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="9px" fontWeight="700" fontFamily="mono"
              color={allComplete ? GOLD : 'whiteAlpha.600'} cursor="pointer"
              border="1px solid"
              borderColor={allComplete ? GOLD + '88' : 'whiteAlpha.300'}
              transition="all 0.2s"
              _hover={{ color: allComplete ? GOLD : ORANGE, borderColor: allComplete ? GOLD : ORANGE_DIM }}
              onClick={onAchOpen}
              title="Achievements"
              {...(allComplete ? {
                animation: 'trophyGlow 3s ease-in-out infinite',
                sx: {
                  '@keyframes trophyGlow': {
                    '0%, 100%': { boxShadow: `0 0 4px ${GOLD}22` },
                    '50%': { boxShadow: `0 0 12px ${GOLD}44` },
                  },
                },
              } : {})}
            >
              <Box as="svg" viewBox="0 0 24 24" w="13px" h="13px" fill="currentColor">
                <path d="M5 3h14v2h-1v1a5 5 0 01-2.1 4.1A3.5 3.5 0 0113.5 13H13v3h2v2h1v2H8v-2h1v-2h2v-3h-.5a3.5 3.5 0 01-2.4-2.9A5 5 0 016 6V5H5V3zm3 2v1a3 3 0 002 2.83V9a1.5 1.5 0 001.5 1.5h1A1.5 1.5 0 0014 9v-.17A3 3 0 0016 6V5H8z"/>
              </Box>
              {achCount > 0 && !allComplete && (
                <Box
                  position="absolute" top="-2px" right="-2px"
                  w="6px" h="6px" borderRadius="full"
                  bg={ORANGE}
                  boxShadow={`0 0 6px ${ORANGE}`}
                />
              )}
            </Box>
            <Box
              as="button"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="9px" fontWeight="700" fontFamily="mono"
              color="whiteAlpha.600" cursor="pointer"
              border="1px solid" borderColor="whiteAlpha.300"
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={() => { onWpOpen(); unlock('whitepaper'); }}
              title="Bitcoin Whitepaper"
            >
              <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm2-6h8v1.5H8V14zm0-3h8v1.5H8V11zm0 6h5v1.5H8V17z"/>
              </Box>
            </Box>
            {!!ENABLE_NODE_GLOBE && (
            <Box
              as="button"
              w="28px" h="28px"
              display="flex" alignItems="center" justifyContent="center"
              fontSize="9px" fontWeight="700" fontFamily="mono"
              color={nodeMapActive ? ORANGE : 'whiteAlpha.600'}
              cursor="pointer"
              border="1px solid"
              borderColor={nodeMapActive ? ORANGE : 'whiteAlpha.300'}
              transition="all 0.2s"
              _hover={{ color: ORANGE, borderColor: ORANGE_DIM }}
              onClick={() => {
                if (nodeMapActive) onResetCamera?.();
                else setTimechainView(false);
                setNodeMapActive(n => !n);
              }}
              title={nodeMapActive ? 'Back to Tesseract' : 'Node Map (N)'}
            >
              {nodeMapActive ? (
                <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </Box>
              ) : (
                <Box as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
                </Box>
              )}
            </Box>
            )}
            <HudToggle
              iconKey="network"
              visible={hudVisibility.network}
              onClick={() => toggleHud('network')}
              title={hudVisibility.network ? 'Hide network panel' : 'Show network panel'}
            />
          </HStack>
        </VStack>
      </Box>

      {}
      {hoveredCubeBlock && hoveredCubeScreenPos && (
        <Box
          position="absolute"
          left={`${hoveredCubeScreenPos.x}px`}
          top={`${hoveredCubeScreenPos.y}px`}
          transform="translate(-50%, -100%)"
          pointerEvents="none"
          zIndex={30}
          px="8px" py="3px"
          bg="rgba(0,0,0,0.5)"
          border="1px solid"
          borderColor={ORANGE}
          fontFamily="mono"
          fontSize="11px"
          fontWeight="700"
          color={ORANGE}
          letterSpacing="0.1em"
          whiteSpace="nowrap"
          textShadow={`0 0 8px ${ORANGE}88`}
        >
          {fmtNum(hoveredCubeBlock.height)}
        </Box>
      )}

      <SatoshiQuoteOverlay isInsideCube={isInsideCube} />
      {explorerBlock && (
        <Box
          key={explorerBlock.height ?? explorerBlock.hash}
          position="absolute"
          top={{ base: 'auto', md: '50%' }}
          bottom={{ base: '140px', md: 'auto' }}
          left={{ base: '50%', md: 'auto' }}
          right={{ base: 'auto', md: '20%' }}
          transform={{ base: 'translateX(-50%)', md: 'translateY(-50%)' }}
          zIndex={20}
          pointerEvents="auto"
          w={{ base: 'calc(100vw - 32px)', md: '320px' }}
          maxW={{ base: '360px', md: '320px' }}
          sx={{
            animation: 'blockCardFade 0.8s ease-out 0.6s both',
            '@keyframes blockCardFade': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
        >
          <BracketPanel accentColor={ORANGE_DIM}>
            <VStack spacing={2} align="stretch">
              <Flex justify="space-between" align="center">
                <Text
                  fontSize="18px" fontFamily="mono" fontWeight="bold"
                  color={explorerBlock.height === 0
                    ? GOLD
                    : (explorerBlock.source === 'mempool' || explorerBlock.source === 'esplora')
                      ? RED_ORANGE
                      : ORANGE}
                  letterSpacing="0.1em"
                >
                  {explorerBlock.height === 0 ? 'GENESIS' : 'BLOCK'} {fmtNum(explorerBlock.height)}
                </Text>
                <Box
                  as="button" fontSize="14px" color="whiteAlpha.600" cursor="pointer"
                  _hover={{ color: 'white' }} onClick={() => { setExplorerBlock(null); setSearchedBlock(null); setGenesisActive(false); onResetCamera?.(); }}
                >
                  ✕
                </Box>
              </Flex>
              {explorerBlock.hash && (
                <TRow label="hash" value={explorerBlock.hash.slice(0, 8) + '...' + explorerBlock.hash.slice(-8)} />
              )}
              {explorerBlock.timestamp && (
                <TRow label="time" value={new Date(explorerBlock.timestamp * 1000).toLocaleString()} />
              )}
              {explorerBlock.pool && <TRow label="miner" value={explorerBlock.pool} color={ORANGE} />}
              {explorerBlock.txCount && <TRow label="txs" value={fmtNum(explorerBlock.txCount)} />}
              {explorerBlock.size && <TRow label="size" value={fmtSize(explorerBlock.size)} />}
              {explorerBlock.weight && <TRow label="weight" value={fmtNum(explorerBlock.weight)} />}
              {explorerBlock.totalFees != null && (
                <TRow label="fees" value={fmtSats(explorerBlock.totalFees) + ' BTC'} color={CYAN} />
              )}
              {explorerBlock.medianFee != null && (
                <TRow label="med fee" value={fmtFeeRate(explorerBlock.medianFee)} />
              )}
              <Rule color="whiteAlpha.300" />
              <TRow
                label="reward"
                value={fmtBtc(50 / Math.pow(2, Math.floor(explorerBlock.height / 210000))) + ' BTC'}
                color={ORANGE}
              />
            </VStack>
          </BracketPanel>
        </Box>
      )}

      {explorerTx && (
        <Box
          key={explorerTx.txid}
          position="absolute"
          top={{ base: 'auto', md: '50%' }}
          bottom={{ base: '140px', md: 'auto' }}
          left={{ base: '50%', md: 'auto' }}
          right={{ base: 'auto', md: '20%' }}
          transform={{ base: 'translateX(-50%)', md: 'translateY(-50%)' }}
          zIndex={20}
          pointerEvents="auto"
          w={{ base: 'calc(100vw - 32px)', md: '320px' }}
          maxW={{ base: '360px', md: '320px' }}
          sx={{
            animation: 'blockCardFade 0.8s ease-out 0.6s both',
          }}
        >
          <BracketPanel accentColor={ORANGE_DIM}>
            <VStack spacing={2} align="stretch">
              <Flex justify="space-between" align="center">
                <Text fontSize="18px" fontFamily="mono" fontWeight="bold" color={RED_ORANGE} letterSpacing="0.1em">
                  TRANSACTION
                </Text>
                <Box
                  as="button" fontSize="14px" color="whiteAlpha.600" cursor="pointer"
                  _hover={{ color: 'white' }}
                  onClick={() => { setExplorerTx(null); setSearchedBlock(null); onResetCamera?.(); }}
                >
                  ✕
                </Box>
              </Flex>
              <HStack spacing={2}>
                {explorerTx.isCoinbase && (
                  <Box px={1} py="1px" border="1px solid" borderColor={ORANGE} fontSize="8px" color={ORANGE} fontFamily="mono" letterSpacing="0.1em">
                    COINBASE
                  </Box>
                )}
                <Box px={1} py="1px" border="1px solid" borderColor={explorerTx.confirmed ? '#34d399' : '#FFD93D'} fontSize="8px" color={explorerTx.confirmed ? '#34d399' : '#FFD93D'} fontFamily="mono" letterSpacing="0.1em">
                  {explorerTx.confirmed ? 'CONFIRMED' : 'IN MEMPOOL'}
                </Box>
              </HStack>
              {explorerTx.totalOut != null && (
                <TRow label="value" value={fmtSats(explorerTx.totalOut, 8) + ' BTC'} color={ORANGE} />
              )}
              {explorerTx.confirmed && explorerTx.blockHeight != null && (
                <TRow label="block" value={'#' + fmtNum(explorerTx.blockHeight)} color={ORANGE} />
              )}
              {!explorerTx.confirmed && trackedTxData?.txid === explorerTx.txid && trackedTxData.position && (
                <TRow
                  label="queue"
                  value={`block ${trackedTxData.position.block} · ${fmtNum(Math.round(trackedTxData.position.vsize / 1000))} kvB deep`}
                  color="#FFD93D"
                />
              )}
              {!explorerTx.confirmed && trackedTxData?.txid === explorerTx.txid && trackedTxData.cpfp?.effectiveFeePerVsize != null && (
                <TRow
                  label="eff rate"
                  value={fmtFeeRate(trackedTxData.cpfp.effectiveFeePerVsize)}
                  color={CYAN}
                />
              )}
              {!explorerTx.confirmed && trackedTxData?.txid === explorerTx.txid && (trackedTxData.cpfp?.ancestors?.length > 0 || trackedTxData.cpfp?.descendants?.length > 0) && (
                <TRow
                  label="cpfp"
                  value={`${trackedTxData.cpfp.ancestors?.length || 0} anc · ${trackedTxData.cpfp.descendants?.length || 0} desc`}
                />
              )}
              {explorerTx.size != null && <TRow label="size" value={fmtNum(explorerTx.size) + ' B'} />}
              {explorerTx.weight != null && <TRow label="weight" value={fmtNum(explorerTx.weight)} />}
              {explorerTx.fee != null && (
                <TRow label="fee" value={fmtSats(explorerTx.fee, 8) + ' BTC'} color={CYAN} />
              )}
              {explorerTx.feeRate != null && (
                <TRow label="rate" value={fmtFeeRate(explorerTx.feeRate)} />
              )}
              <TRow
                label="inputs"
                value={fmtNum(explorerTx.vinCount) + (explorerTx.totalIn ? ' / ' + fmtSats(explorerTx.totalIn) + ' BTC' : '')}
              />
              <TRow
                label="outputs"
                value={fmtNum(explorerTx.voutCount) + ' / ' + fmtSats(explorerTx.totalOut) + ' BTC'}
              />
              {explorerTx.confirmed && explorerTx.blockTime && (
                <TRow label="mined" value={fmtBlockDate(explorerTx.blockTime)} />
              )}
              <Rule color="whiteAlpha.300" />
              <Box>
                <TLabel color="whiteAlpha.600">TXID</TLabel>
                <Text mt={1} fontSize="9px" color="whiteAlpha.600" fontFamily="mono" wordBreak="break-all" letterSpacing="0.02em">
                  {explorerTx.txid}
                </Text>
              </Box>
            </VStack>
          </BracketPanel>
        </Box>
      )}

      {(satsGame || satsFinalScore !== null) && (
        <Flex
          position="absolute"
          top={{ base: '8px', md: '14px' }}
          left="50%"
          transform="translateX(-50%)"
          zIndex={20}
          pointerEvents="none"
        >
          <BracketPanel accentColor={ORANGE}>
            <VStack spacing={1} align="center" minW="180px">
              {satsGame ? (
                <>
                  <Text fontSize="14px" fontFamily="mono" fontWeight="bold" color={ORANGE}
                    textShadow={`0 0 10px ${ORANGE}88`}>
                    STACK SATS!
                  </Text>
                  <Text fontSize="28px" fontFamily="mono" fontWeight="bold" color="white">
                    {satsScore.toLocaleString()}
                  </Text>
                  <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.600">
                    {satsTimeLeft}s remaining
                  </Text>
                </>
              ) : (
                <>
                  <Text fontSize="12px" fontFamily="mono" fontWeight="bold" color={ORANGE}
                    textShadow={`0 0 10px ${ORANGE}88`}>
                    FINAL SCORE
                  </Text>
                  <Text fontSize="32px" fontFamily="mono" fontWeight="bold" color="white"
                    textShadow="0 0 20px rgba(247, 147, 26, 0.5)">
                    {satsFinalScore.toLocaleString()}
                  </Text>
                  <Text fontSize="10px" fontFamily="mono" color="whiteAlpha.600">
                    sats stacked
                  </Text>
                </>
              )}
            </VStack>
          </BracketPanel>
        </Flex>
      )}

      {satsPickups.map(p => (
        <Text
          key={p.id}
          position="fixed"
          left={`${p.x}px`}
          top={`${p.y}px`}
          fontSize="18px"
          fontFamily="mono"
          fontWeight="bold"
          color={ORANGE}
          pointerEvents="none"
          zIndex={100}
          textShadow={`0 0 8px ${ORANGE}88, 0 0 16px ${ORANGE}44`}
          animation="satsFloat 0.9s ease-out forwards"
          sx={{
            '@keyframes satsFloat': {
              '0%': { opacity: 1, transform: 'translate(-50%, -50%) scale(0.8)' },
              '20%': { opacity: 1, transform: 'translate(-50%, -80%) scale(1.1)' },
              '100%': { opacity: 0, transform: 'translate(-50%, -180%) scale(0.9)' },
            },
          }}
        >
          +{p.amt.toLocaleString()}
        </Text>
      ))}

      <GenesisOverlay active={genesisActive} onClose={() => setGenesisActive(false)} />
      <PizzaDayToast active={pizzaDay} onClose={() => setPizzaDay(false)} />
      <MilestoneOverlay active={milestone} blockHeight={block?.height} />

      {!blockClickHintDismissed
        && !explorerBlock
        && !genesisActive
        && !searchedBlock
        && !isTourOpen
        && !konamiSlogan
        && !satsGame
        && blockHistory.length > 0 && (
        <Box
          position="absolute"
          bottom={{ base: '230px', md: '200px' }}
          left="50%"
          transform="translateX(-50%)"
          zIndex={20}
          pointerEvents="auto"
          animation="hintFadeIn 0.6s ease-out"
          sx={{
            '@keyframes hintFadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
        >
          <BracketPanel accentColor={ORANGE_DIM}>
            <HStack spacing={2} align="center">
              <Box
                as="svg" viewBox="0 0 24 24" w="14px" h="14px" fill="none"
                stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                animation="hintBounce 1.6s ease-in-out infinite"
                sx={{
                  '@keyframes hintBounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-3px)' },
                  },
                }}
              >
                <polyline points="18 15 12 9 6 15" />
              </Box>
              <Text fontSize="10px" color="whiteAlpha.900" fontFamily="mono"
                letterSpacing="0.18em" textTransform="uppercase" whiteSpace="nowrap">
                {isMobile ? 'Tap' : 'Click'} any cube to fly inside
              </Text>
              <Box
                as="button"
                onClick={() => setBlockClickHintDismissed(true)}
                color="whiteAlpha.500"
                fontSize="14px" lineHeight="1"
                ml={1}
                _hover={{ color: ORANGE }}
                aria-label="Dismiss hint"
                cursor="pointer"
              >
                &#x2715;
              </Box>
            </HStack>
          </BracketPanel>
        </Box>
      )}

      <InfoModal isOpen={isOpen} onClose={onClose} onReplayTour={ENABLE_TOUR ? replayTour : null} />
      <InstallModal isOpen={isInstallOpen} onClose={onInstallClose} />
      <WhitepaperModal isOpen={isWpOpen} onClose={onWpClose} />
      <AchievementsModal isOpen={isAchOpen} onClose={onAchClose} achievements={achievements} count={achCount} allComplete={allComplete} />
      <SearchModal isOpen={isSearchOpen} onClose={handleSearchClose} initialQuery={searchInitialQuery} />
      <FeeHeatmapModal isOpen={isFeeMapOpen} onClose={onFeeMapClose} blocks={mempoolBlocks} fees={fees} liveBlock0TxCount={liveBlock0TxCount} onTrackMempoolBlock={trackMempoolBlock} />
      <NotificationHelpModal isOpen={isNotifHelpOpen} onClose={onNotifHelpClose} permission={notif.permission} />
      <OnboardingTourModal isOpen={isTourOpen} onClose={completeTour} onSkip={completeTour} />
      <InstallBanner visible={showInstallBanner} onInstall={acceptInstallBanner} onDismiss={dismissInstallBanner} />
    </Box>
  );
};
